import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react';
import { useParams, Link, useSearch, useLocation } from 'wouter';
import { AlertCircle, Lock, CheckCircle2, Plus, ArrowRight, AlertTriangle, Users, GitBranch, X, RefreshCw, Clock, Sparkles, Award, TrendingUp, TrendingDown, Minus, FileText, Tag, Zap, Loader2, BarChart3, Pencil, Trash2, Check, Search, ChevronLeft, ChevronRight, CalendarDays, List } from 'lucide-react';
import { TfaLockBanner } from './tfa-lock-banner.js';
import { TfaDeliberationWorkbench } from './tfa-deliberation-workbench.js';
import { TfaSynthesisView } from './tfa-synthesis-view.js';
import { TimeEntryDetailPanel } from '@/pages/timewriting/time-entry-slide-panel.js';
import { TimelineTab } from './timeline-tab.js';
import { DocumentTimeline } from './document-timeline.js';
import { EligibilityTab } from './eligibility-tab.js';
import { DefensibilityWidget } from './defensibility-widget.js';
import { SdlcBreakdown } from '@/components/sdlc-breakdown.js';
import { useAuthStore } from '@/store/auth-store.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { Card } from '@/components/ui/card.js';
import { SlidePanel } from '@/components/ui/slide-panel.js';
import { MarkdownContent } from '@/components/ui/markdown-content.js';
import { TermTooltip } from '@/components/tour/index.js';
import { useAuth } from '@clerk/react';
import { formatLabel, formatDeveloper, formatFy } from '@/lib/utils.js';
import type { Project, Activity } from '@edison/api-client-react';
import {
  useGetProject,
  useGetTfa,
  useCreateProjectTfa,
  usePatchTfaStage,
  usePatchTfaContent,
  usePostTfaLock,
  usePostTfaUnlock,
  usePostTfaPanelist,
  usePostTfaPanelistConfirm,
  useDeleteTfaPanelist,
  usePostTfaDetermination,
  useGetProjectActivities,
  useGetProjectRepos,
  useAddProjectRepo,
  useRemoveProjectRepo,
  useGetSignalAvailableRepos,
  useGetTimeEntries,
  useApproveTimeEntry,
  useRejectTimeEntry,
  useUndoTimeEntryRejection,
  useGetUsers,
  getGetTfaQueryKey,
  getGetProjectQueryKey,
  getGetProjectsQueryKey,
  useUpdateProject,
  useDeleteProject,
  useCloseProject,
  useRegenerateTfaDraft,
  usePatchTfaPanelistFinalApprover,
  useGetTfaDeliberations,
  getGetTfaDeliberationsQueryKey,
  usePostTfaSynthesize,
  customInstance,
  type TfaPanelFull,
  type PatchTfaStageBodyStage,
} from '@edison/api-client-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const TFA_STAGES = [
  { key: 'setup', label: 'Setup' },
  { key: 'problem_definition', label: 'Problem Definition' },
  { key: 'cots_sweep', label: 'COTS Sweep' },
  { key: 'panel_selection', label: 'Panel Selection' },
  { key: 'panel_deliberation', label: 'Panel Deliberation' },
  { key: 'determination', label: 'Determination' },
] as const;

function TfaStageProgress({ stage, locked, onStageClick }: { stage: string; locked: boolean; onStageClick?: (key: string) => void }) {
  const currentIdx = TFA_STAGES.findIndex((s) => s.key === stage);
  return (
    <div className="mb-6">
      <div className="flex items-start">
        {TFA_STAGES.map((s, i) => {
          const done = i < currentIdx || (i === currentIdx && locked);
          const active = i === currentIdx && !locked;
          const clickable = done && !locked && !!onStageClick;
          return (
            <div key={s.key} className="flex items-start flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-2">
                <div
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={() => clickable && onStageClick(s.key)}
                  onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) onStageClick(s.key); }}
                  className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 shrink-0 ${
                    done
                      ? `bg-emerald-500 border-emerald-500 text-white${clickable ? ' cursor-pointer hover:bg-emerald-600 hover:border-emerald-600' : ''}`
                      : active
                      ? 'bg-background border-primary text-primary'
                      : 'bg-muted border-muted-foreground/25 text-muted-foreground/50'
                  }`}
                >
                  {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </div>
                <span
                  className={`text-xs leading-tight text-center w-16 ${
                    active ? 'text-foreground font-medium' : done ? `text-emerald-700${clickable ? ' cursor-pointer' : ''}` : 'text-muted-foreground/60'
                  }`}
                  onClick={() => clickable && onStageClick(s.key)}
                >
                  {s.label}
                </span>
              </div>
              {i < TFA_STAGES.length - 1 && (
                <div
                  className={`h-px flex-1 mx-1 mt-4 ${
                    i < currentIdx ? 'bg-emerald-400' : 'bg-muted-foreground/20'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
      {!locked && onStageClick && currentIdx > 0 && (
        <p className="text-xs text-muted-foreground mt-2">Click a completed step to navigate back</p>
      )}
    </div>
  );
}

interface CotsSweepItem {
  name: string;
  whyConsidered: string;
  whyRejected: string;
}

interface AiDraftCardProps {
  status: string | null | undefined;
  draftText: string | null | undefined;
  locked: boolean;
  onUseDraft: (text: string) => void;
  onRegenerate: () => void;
  isRegenerating: boolean;
  sweepItems?: CotsSweepItem[] | null;
}

const REGEN_STAGES = [
  'Step 1/4 — Reading PRD…',
  'Step 2/4 — Assembling context…',
  'Step 3/4 — Calling Claude…',
  'Step 4/4 — Writing draft…',
];

function AiDraftCard({
  status,
  draftText,
  locked,
  onUseDraft,
  onRegenerate,
  isRegenerating,
  sweepItems,
}: AiDraftCardProps) {
  const [open, setOpen] = useState(false);
  const [regenStage, setRegenStage] = useState(0);

  // Cycle through stages while pending (either HTTP in-flight or async worker running)
  const isPending = isRegenerating || status === 'pending';
  useEffect(() => {
    if (!isPending) { setRegenStage(0); return; }
    setRegenStage(0);
    const t1 = setTimeout(() => setRegenStage(1), 1500);
    const t2 = setTimeout(() => setRegenStage(2), 3500);
    // Hold at step 3 until polling detects completion — don't advance to step 4 on a timer
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isPending]);

  // null/undefined = pre-feature or ungenerated — show a generate prompt
  if (status == null) {
    return (
      <div className="rounded-lg border border-dashed mt-3 px-3 py-2 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-violet-500" aria-hidden="true" />
          <span>Generate AI draft from PRD</span>
        </span>
        {!locked && (
          <button
            type="button"
            className="text-xs text-primary hover:underline disabled:opacity-50"
            onClick={onRegenerate}
            disabled={isPending}
          >
            {isPending ? (
              <span className="flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                {REGEN_STAGES[regenStage]}
              </span>
            ) : 'Generate'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-dashed mt-3">
      <div
        className="w-full flex items-center justify-between px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
        aria-expanded={open}
        aria-label={open ? 'Collapse AI draft' : 'Expand AI draft'}
      >
        <span className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-violet-500" aria-hidden="true" />
          <span className="font-medium text-foreground">AI draft</span>
          <span className="text-xs text-muted-foreground">· from PRD.md</span>
        </span>
        <span className="flex items-center gap-2">
          {!locked && (
            status === 'pending' ? (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                {REGEN_STAGES[regenStage]}
              </span>
            ) : (
              <button
                type="button"
                className="text-xs text-primary hover:underline disabled:opacity-50"
                onClick={(e) => { e.stopPropagation(); onRegenerate(); }}
                disabled={isRegenerating}
                aria-label="Regenerate AI draft"
              >
                {isRegenerating ? (
                  <span className="flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                    {REGEN_STAGES[regenStage]}
                  </span>
                ) : 'Regenerate'}
              </button>
            )
          )}
          <span aria-hidden="true">{open ? '▲' : '▼'}</span>
        </span>
      </div>

      {open && (
        <div className="px-3 pb-3 space-y-2 border-t pt-2">
          {status === 'pending' ? (
            <div className="space-y-2" aria-label="Generating AI draft">
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                {REGEN_STAGES[regenStage]}
              </p>
              <div className="h-4 w-3/4 rounded skeleton" />
              <div className="h-4 w-full rounded skeleton" />
              <div className="h-4 w-2/3 rounded skeleton" />
            </div>
          ) : status === 'skipped' ? (
            <p className="text-xs text-muted-foreground">
              No <code className="font-mono bg-muted px-0.5 rounded">PRD.md</code> found in linked repositories. Add one to the root or <code className="font-mono bg-muted px-0.5 rounded">docs/</code> directory of your GitHub repo, then{' '}
              {!locked ? (
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={onRegenerate}
                  disabled={isRegenerating}
                >
                  regenerate
                </button>
              ) : 'regenerate'}.
            </p>
          ) : status === 'failed' ? (
            <p className="text-xs text-muted-foreground">
              AI draft generation failed.{' '}
              {!locked && (
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={onRegenerate}
                  disabled={isRegenerating}
                >
                  Retry
                </button>
              )}
            </p>
          ) : draftText ? (
            <>
              <pre className="text-xs whitespace-pre-wrap text-muted-foreground bg-muted/50 rounded p-2 max-h-48 overflow-y-auto">
                {draftText}
              </pre>
              {sweepItems && sweepItems.length > 0 && (
                <details className="group">
                  <summary className="text-xs text-muted-foreground cursor-pointer select-none hover:text-foreground flex items-center gap-1 list-none">
                    <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
                    Research trail — {sweepItems.length} solution{sweepItems.length !== 1 ? 's' : ''} evaluated
                  </summary>
                  <ul className="mt-2 space-y-2">
                    {sweepItems.map((item, i) => (
                      <li key={i} className="rounded border bg-muted/30 px-3 py-2 text-xs space-y-1">
                        <p className="font-medium text-foreground">{item.name}</p>
                        <p className="text-muted-foreground">
                          <span className="text-amber-600 mr-1">?</span>{item.whyConsidered}
                        </p>
                        <p className="text-muted-foreground">
                          <span className="text-destructive mr-1">✗</span>{item.whyRejected}
                        </p>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={() => onUseDraft(draftText)}
                aria-label="Use this AI draft — copies into the text area above"
              >
                Use this draft ↑
              </Button>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Derive a suggested determination label from the AI synthesis text. */
function suggestDetermination(synthesis: string | null | undefined): 'uncertain' | 'partially_uncertain' | 'certain' {
  if (!synthesis) return 'uncertain';
  const text = synthesis.toLowerCase();
  if (text.includes('partially uncertain')) return 'partially_uncertain';
  if (text.includes('uncertain')) return 'uncertain';
  if (text.includes('certain')) return 'certain';
  return 'uncertain';
}

function TfaPanel({ tfa, projectId, onSaveFlash }: { tfa: TfaPanelFull; projectId: string; onSaveFlash?: (msg: string) => void }) {
  const queryClient = useQueryClient();
  const invalidateTfa = () => {
    void queryClient.invalidateQueries({ queryKey: getGetTfaQueryKey(tfa.id) });
    void queryClient.invalidateQueries({ queryKey: getGetTfaDeliberationsQueryKey(tfa.id) });
  };
  const { data: reposData } = useGetProjectRepos(projectId);
  const repos = reposData?.data?.items ?? [];
  const flashAndInvalidate = (msg: string) => {
    invalidateTfa();
    onSaveFlash?.(msg);
  };

  const patchStage = usePatchTfaStage();
  const patchContent = usePatchTfaContent();
  const postLock = usePostTfaLock();
  const postUnlock = usePostTfaUnlock();
  const postPanelist = usePostTfaPanelist();
  const confirmPanelist = usePostTfaPanelistConfirm();
  const deletePanelist = useDeleteTfaPanelist();
  const postDetermination = usePostTfaDetermination();

  const regenerateDraft = useRegenerateTfaDraft({
    mutation: {
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: getGetTfaQueryKey(tfa.id) }),
    },
  });

  type CotsSolution = { name: string; category: string; why_insufficient: string };
  type CotsResult = { solutions: CotsSolution[]; summary: string };
  const [cotsResult, setCotsResult] = useState<CotsResult | null>(null);
  const [cotsSearching, setCotsSearching] = useState(false);
  const [cotsError, setCotsError] = useState<string | null>(null);

  const handleCotsSearch = async () => {
    setCotsSearching(true);
    setCotsError(null);
    try {
      const res = await customInstance<{ data: CotsResult }>({
        url: `/api/tfa/${tfa.id}/cots-suggestions`,
        method: 'POST',
      });
      setCotsResult(res.data);
    } catch {
      setCotsError('Failed to generate COTS suggestions. Please try again.');
    } finally {
      setCotsSearching(false);
    }
  };

  const { data: usersData } = useGetUsers();
  // Build user lookup map for dropdown + name display
  const userMap = new Map(
    (usersData?.data?.items ?? [])
      .filter((u): u is typeof u & { id: string; email: string } => typeof u.id === 'string')
      .map((u) => [u.id, u.name ?? u.email])
  );

  const contentForm = useForm<{ problemStatement: string; cotsSweepNotes: string; assessmentDate: string }>();
  const panelistForm = useForm<{ userId: string }>();
  const determinationForm = useForm<{ determination: string; notes: string }>({
    defaultValues: { determination: suggestDetermination(tfa.aiDeterminationSynthesis), notes: '' },
  });

  const handleUseDraft = useCallback((field: 'problem_statement' | 'cots_sweep', text: string) => {
    if (field === 'problem_statement') {
      contentForm.setValue('problemStatement', text, { shouldDirty: true });
    } else {
      contentForm.setValue('cotsSweepNotes', text, { shouldDirty: true });
    }
  }, [contentForm]);

  const currentStageIdx = TFA_STAGES.findIndex((s) => s.key === tfa.stage);
  const nextStage = TFA_STAGES[currentStageIdx + 1];
  const prevStage = TFA_STAGES[currentStageIdx - 1];

  const canLock =
    tfa.stage === 'determination' &&
    !tfa.locked &&
    tfa.determination !== undefined &&
    tfa.panelists.every((p) => p.confirmedAt);

  const allPanelistsConfirmed =
    tfa.panelists.length > 0 && tfa.panelists.every((p) => p.confirmedAt);

  const { user } = useAuthStore();
  const setPanelistFinalApprover = usePatchTfaPanelistFinalApprover();
  const synthesize = usePostTfaSynthesize();
  const { data: deliberationsData } = useGetTfaDeliberations(
    tfa.id,
    { query: { queryKey: getGetTfaDeliberationsQueryKey(tfa.id), enabled: tfa.stage === 'panel_selection' || tfa.stage === 'panel_deliberation' || tfa.stage === 'determination' } }
  );
  const deliberations = deliberationsData?.data?.items ?? [];

  const myPanelist = tfa.panelists.find((p) => p.userId === user?.id);
  const hasFinalApprover = tfa.panelists.some((p) => p.isFinalApprover);
  const iAmFinalApprover = tfa.panelists.some((p) => p.userId === user?.id && p.isFinalApprover);

  return (
    <Card variant="static" data-tour="tfa-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold flex items-center gap-2">
          <TermTooltip term="TFA">Technical Feasibility Assessment</TermTooltip>
          {tfa.locked && (
            <Badge variant="positive">
              <Lock className="h-3 w-3" />
              Locked
            </Badge>
          )}
        </h3>
        {!tfa.locked && canLock && (
          <Button
            size="sm"
            onClick={() => postLock.mutate({ id: tfa.id }, { onSuccess: () => flashAndInvalidate('TFA locked and finalised') })}
            disabled={postLock.isPending}
            data-tour="lock-tfa"
          >
            <Lock className="h-3 w-3" />
            {postLock.isPending ? 'Locking…' : 'Lock & finalise TFA'}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-5">
        The TFA panel formally assesses whether your R&D project involves genuine technical uncertainty — a prerequisite for AusIndustry R&DTI eligibility.
      </p>

      <TfaStageProgress
        stage={tfa.stage}
        locked={tfa.locked}
        onStageClick={!tfa.locked ? (key) => patchStage.mutate({ id: tfa.id, stage: key as PatchTfaStageBodyStage }, { onSuccess: invalidateTfa }) : undefined}
      />

      {tfa.locked && (
        <div className="mb-4">
          <TfaLockBanner
            lockedAt={tfa.lockedAt ?? null}
            lockedByName={userMap.get(tfa.lockedBy ?? '') ?? tfa.lockedBy ?? null}
            lockedByRole={null}
          />
        </div>
      )}

      {tfa.determination?.determination === 'certain' && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-1" />
          <span>
            <strong>Ineligible for R&D Classification</strong> — The panel determined there is no technical uncertainty. Activities under this project cannot be classified as R&D.
          </span>
        </div>
      )}

      {/* ── SETUP ─────────────────────────────────────────────── */}
      {tfa.stage === 'setup' && !tfa.locked && (
        <div className="space-y-4 border-t pt-4">
          <div>
            <p className="text-sm font-medium mb-1">What is a TFA panel?</p>
            <p className="text-sm text-muted-foreground">
              A Technical Feasibility Assessment is a structured review by qualified technical experts
              who confirm that your project required solving a problem where the outcome could not be
              known in advance — i.e., genuine <em>technical uncertainty</em> existed.
            </p>
          </div>
          <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-2">
            <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-2">Before you continue, confirm:</p>
            <p className="text-sm">✔ You know the income year this project covers ({tfa.retrospective ? 'retrospective' : 'current year'})</p>
            <p className="text-sm">✔ You have at least one qualified technical expert who can sit on the panel</p>
            <p className="text-sm">✔ You are ready to describe the technical problem in writing</p>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => nextStage && patchStage.mutate({ id: tfa.id, stage: nextStage.key }, { onSuccess: invalidateTfa })}
              disabled={patchStage.isPending || !nextStage}
            >
              Start: Define the problem
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── PROBLEM DEFINITION ───────────────────────────────── */}
      {tfa.stage === 'problem_definition' && !tfa.locked && (
        <div className="space-y-4 border-t pt-4">
          <div>
            <label htmlFor="tfa-problem-statement" className="text-sm font-medium mb-1 block">Describe the technical problem</label>
            <p className="text-sm text-muted-foreground mb-3">
              Explain what technical knowledge gap or challenge your project was trying to overcome.
              AusIndustry requires that a competent professional in the field could not have determined
              the outcome without conducting the experimental activity.
            </p>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 mb-3">
              <strong>Tip:</strong> Focus on <em>what was unknown</em>, not what you built.
              E.g. "It was unknown whether [approach X] could achieve [outcome Y] at scale
              given [constraint Z]."
            </div>
            <textarea
              id="tfa-problem-statement"
              rows={6}
              {...contentForm.register('problemStatement')}
              defaultValue={tfa.problemStatement ?? ''}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-y"
              placeholder="Describe the specific technical problem your project attempted to solve, and why the outcome could not be determined without experimentation…"
            />
            <AiDraftCard
              status={tfa.aiDraftStatus}
              draftText={tfa.aiDraftProblemStatement}
              locked={tfa.locked}
              onUseDraft={(text) => handleUseDraft('problem_statement', text)}
              onRegenerate={() => regenerateDraft.mutate({ id: tfa.id })}
              isRegenerating={regenerateDraft.isPending}
            />
            {repos.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                {repos.map((r) => (
                  <span key={r.id} className={`flex items-center gap-1 text-xs ${r.hasPrd ? 'text-emerald-600' : 'text-muted-foreground/50'}`} title={r.hasPrd ? 'PRD.md found — will be used for AI draft' : 'No PRD.md — will be skipped'}>
                    <FileText className="h-3 w-3" aria-hidden="true" />
                    {r.repoOwner}/{r.repoName}
                    {r.hasPrd ? '' : ' (no PRD)'}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => prevStage && patchStage.mutate({ id: tfa.id, stage: prevStage.key }, { onSuccess: invalidateTfa })}
              disabled={patchStage.isPending}
            >
              ← Back to Setup
            </Button>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => contentForm.handleSubmit((d) => patchContent.mutate({ id: tfa.id, problemStatement: d.problemStatement }, { onSuccess: () => flashAndInvalidate('Saved') }))()}
                disabled={patchContent.isPending}
              >
                {patchContent.isPending ? 'Saving…' : 'Save draft'}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (!nextStage) return;
                  const values = contentForm.getValues();
                  patchContent.mutate(
                    { id: tfa.id, problemStatement: values.problemStatement },
                    { onSuccess: () => patchStage.mutate({ id: tfa.id, stage: nextStage.key }, { onSuccess: invalidateTfa }) }
                  );
                }}
                disabled={patchStage.isPending || patchContent.isPending || !nextStage}
              >
                Next: COTS Sweep
                <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── COTS SWEEP ───────────────────────────────────────── */}
      {tfa.stage === 'cots_sweep' && !tfa.locked && (
        <div className="space-y-4 border-t pt-4">
          <div>
            <label htmlFor="tfa-cots-sweep" className="text-sm font-medium mb-1 block">Commercial Off-The-Shelf (COTS) Sweep</label>
            <p className="text-sm text-muted-foreground mb-3">
              Document your search for existing products, solutions, or public knowledge that could
              have solved your problem without original R&D. AusIndustry requires that you confirm no
              commercially available solution existed that a competent professional could have applied.
            </p>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 mb-3">
              <strong>What to include:</strong> Products and vendors evaluated, academic or industry
              literature reviewed, and a clear explanation of why each was insufficient to resolve
              your specific technical problem.
            </div>
            <textarea
              id="tfa-cots-sweep"
              rows={6}
              {...contentForm.register('cotsSweepNotes')}
              defaultValue={tfa.cotsSweepNotes ?? ''}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-y"
              placeholder="List the commercial solutions, open-source tools, and public knowledge sources reviewed, and explain why none of them resolved the technical problem described above…"
            />
            <AiDraftCard
              status={tfa.aiDraftStatus}
              draftText={tfa.aiDraftCotsSweepNotes}
              locked={tfa.locked}
              onUseDraft={(text) => handleUseDraft('cots_sweep', text)}
              onRegenerate={() => regenerateDraft.mutate({ id: tfa.id })}
              isRegenerating={regenerateDraft.isPending}
              sweepItems={tfa.aiDraftCotsSweepItems as CotsSweepItem[] | null | undefined}
            />

            {/* AI COTS research */}
            {!tfa.locked && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => void handleCotsSearch()}
                  disabled={cotsSearching || !tfa.problemStatement}
                  className="flex items-center gap-2 text-sm text-primary hover:underline disabled:opacity-50 disabled:no-underline"
                  title={!tfa.problemStatement ? 'Complete the Problem Statement first' : undefined}
                >
                  {cotsSearching ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />Researching COTS solutions…</>
                  ) : (
                    <><Sparkles className="h-3.5 w-3.5" aria-hidden="true" />AI research COTS solutions</>
                  )}
                </button>
                {cotsError && <p className="text-xs text-destructive mt-1">{cotsError}</p>}
                {cotsResult && (
                  <div className="mt-3 rounded-lg border bg-muted/30 p-3 space-y-3">
                    <p className="text-xs text-muted-foreground">{cotsResult.summary}</p>
                    <ul className="space-y-2">
                      {cotsResult.solutions.map((s, i) => (
                        <li key={i} className="text-xs border-l-2 border-muted-foreground/20 pl-3 space-y-0.5">
                          <div className="font-medium text-foreground">{s.name}</div>
                          <div className="text-muted-foreground">{s.category}</div>
                          <div className="text-muted-foreground">{s.why_insufficient}</div>
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => {
                        const existing = contentForm.getValues('cotsSweepNotes') ?? '';
                        const lines = cotsResult.solutions
                          .map((s) => `• ${s.name} (${s.category}): ${s.why_insufficient}`)
                          .join('\n');
                        const appended = existing ? `${existing}\n\n${lines}` : lines;
                        contentForm.setValue('cotsSweepNotes', appended);
                      }}
                    >
                      Append to notes ↑
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => prevStage && patchStage.mutate({ id: tfa.id, stage: prevStage.key }, { onSuccess: invalidateTfa })}
              disabled={patchStage.isPending}
            >
              ← Back to Problem Definition
            </Button>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => contentForm.handleSubmit((d) => patchContent.mutate({ id: tfa.id, cotsSweepNotes: d.cotsSweepNotes }, { onSuccess: () => flashAndInvalidate('Saved') }))()}
                disabled={patchContent.isPending}
              >
                {patchContent.isPending ? 'Saving…' : 'Save draft'}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (!nextStage) return;
                  const values = contentForm.getValues();
                  patchContent.mutate(
                    { id: tfa.id, cotsSweepNotes: values.cotsSweepNotes },
                    { onSuccess: () => patchStage.mutate({ id: tfa.id, stage: nextStage.key }, { onSuccess: invalidateTfa }) }
                  );
                }}
                disabled={patchStage.isPending || patchContent.isPending || !nextStage}
              >
                Next: Panel Selection
                <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── PANEL SELECTION ─────────────────────────────────── */}
      {tfa.stage === 'panel_selection' && (
        <div className="space-y-4 border-t pt-4">
          <div>
            <p className="text-sm font-medium mb-1">Compose your TFA panel</p>
            <p className="text-sm text-muted-foreground mb-3">
              Add panel members, have each confirm their participation, then designate a Final Approver who will record the formal determination and lock the TFA.
            </p>
          </div>

          {tfa.panelists.length === 0 ? (
            <div className="rounded-lg border border-dashed p-4 text-center">
              <Users className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No panelists added yet.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {tfa.panelists.map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded-lg border px-3 py-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{userMap.get(p.userId ?? '') ?? p.userId ?? 'Unknown'}</span>
                    {p.isFinalApprover && (
                      <span className="text-xs bg-primary/10 text-primary font-medium px-2 py-0.5 rounded-full">Final Approver</span>
                    )}
                    {(p as { profileComplete?: boolean }).profileComplete === false && (
                      <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                        No profile
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {p.confirmedAt ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Confirmed
                      </span>
                    ) : (
                      !tfa.locked && p.userId === user?.id && (
                        <button
                          onClick={() => confirmPanelist.mutate({ panelId: tfa.id, panelistId: p.id }, { onSuccess: () => flashAndInvalidate('Confirmed') })}
                          className="rounded border px-2 py-0.5 text-xs text-primary hover:bg-accent"
                        >
                          Confirm participation
                        </button>
                      )
                    )}
                    {!tfa.locked && (
                      <button
                        onClick={() => setPanelistFinalApprover.mutate(
                          { panelId: tfa.id, panelistId: p.id, data: { isFinalApprover: !p.isFinalApprover } },
                          { onSuccess: invalidateTfa }
                        )}
                        className={`rounded border px-2 py-0.5 text-xs hover:bg-accent ${p.isFinalApprover ? 'text-primary border-primary/30' : 'text-muted-foreground'}`}
                        title={p.isFinalApprover ? 'Remove final approver role' : 'Make final approver'}
                      >
                        {p.isFinalApprover ? 'Final approver ✓' : 'Set as final approver'}
                      </button>
                    )}
                    {!tfa.locked && (
                      <button
                        onClick={() => deletePanelist.mutate({ panelId: tfa.id, panelistId: p.id }, { onSuccess: invalidateTfa })}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label="Remove panelist"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {!tfa.locked && (
            <form
              onSubmit={panelistForm.handleSubmit((d) =>
                postPanelist.mutate(
                  { id: tfa.id, userId: d.userId },
                  { onSuccess: () => { panelistForm.reset(); invalidateTfa(); } }
                )
              )}
              className="rounded-lg bg-muted/40 p-3 space-y-2"
            >
              <label htmlFor="add-panelist-select" className="text-xs font-medium text-muted-foreground block">Add a panel member</label>
              <div className="flex gap-2 flex-wrap">
                <select
                  id="add-panelist-select"
                  {...panelistForm.register('userId', { required: true })}
                  className="flex-1 min-w-48 rounded-lg border bg-background px-3 py-2 text-sm"
                  defaultValue=""
                >
                  <option value="" disabled>Select a team member…</option>
                  {(usersData?.data?.items ?? [])
                    .filter((u): u is typeof u & { id: string } => typeof u.id === 'string')
                    .filter((u) => u.active !== false)
                    .filter((u) => !tfa.panelists.some((p) => p.userId === u.id))
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name ?? u.email}
                      </option>
                    ))
                  }
                </select>
                <Button type="submit" size="sm" disabled={postPanelist.isPending}>
                  {postPanelist.isPending ? 'Adding…' : 'Add panelist'}
                </Button>
              </div>
            </form>
          )}

          {/* Confirmed panelist can start deliberating immediately, no need to wait for others */}
          {myPanelist?.confirmedAt && (
            <TfaDeliberationWorkbench
              tfa={tfa}
              myPanelistId={myPanelist.id}
              onSubmitted={invalidateTfa}
            />
          )}

          {!tfa.locked && !hasFinalApprover && tfa.panelists.length > 0 && (
            <p className="text-xs text-amber-700">Designate a final approver before starting deliberation.</p>
          )}

          {!tfa.locked && (
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => prevStage && patchStage.mutate({ id: tfa.id, stage: prevStage.key }, { onSuccess: invalidateTfa })}
                disabled={patchStage.isPending}
              >
                ← Back to COTS Sweep
              </Button>
              <Button
                size="sm"
                onClick={() => nextStage && patchStage.mutate({ id: tfa.id, stage: nextStage.key }, { onSuccess: invalidateTfa })}
                disabled={patchStage.isPending || !nextStage || !allPanelistsConfirmed || !hasFinalApprover}
                title={
                  !allPanelistsConfirmed ? 'All panelists must confirm first' :
                  !hasFinalApprover ? 'Designate a final approver first' : undefined
                }
              >
                Start Deliberation
                <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── PANEL DELIBERATION ───────────────────────────────── */}
      {tfa.stage === 'panel_deliberation' && (
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium mb-1">Panel Deliberation</p>
              <p className="text-sm text-muted-foreground">
                Each panelist reviews the problem definition and COTS sweep, then submits their structured assessment.
              </p>
            </div>
          </div>

          {/* Submission status summary */}
          <div className="rounded-lg border p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Submission Status</p>
            <ul className="space-y-1">
              {tfa.panelists.map((p) => {
                const submitted = deliberations.some((d) => d.panelistId === p.id && d.submittedAt);
                return (
                  <li key={p.id} className="flex items-center justify-between text-sm">
                    <span>{userMap.get(p.userId ?? '') ?? 'Unknown'}</span>
                    <div className="flex items-center gap-2">
                      {submitted ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Submitted
                        </span>
                      ) : (
                        <span className="text-xs text-amber-600 font-medium">Pending</span>
                      )}
                      {!tfa.locked && (
                        <button
                          onClick={() => deletePanelist.mutate({ panelId: tfa.id, panelistId: p.id }, { onSuccess: invalidateTfa })}
                          className="text-muted-foreground hover:text-destructive"
                          aria-label="Remove panelist"
                          title="Remove panel member"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Per-panelist workbench */}
          {myPanelist && (
            <TfaDeliberationWorkbench
              tfa={tfa}
              myPanelistId={myPanelist.id}
              onSubmitted={invalidateTfa}
            />
          )}

          {!tfa.locked && (
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => prevStage && patchStage.mutate({ id: tfa.id, stage: prevStage.key }, { onSuccess: invalidateTfa })}
                disabled={patchStage.isPending}
              >
                ← Back to Panel Selection
              </Button>
              <Button
                size="sm"
                onClick={async () => {
                  if (!nextStage) return;
                  await patchStage.mutateAsync({ id: tfa.id, stage: nextStage.key });
                  // Only generate synthesis if not already cached
                  if (!tfa.aiDeterminationSynthesis) {
                    try { await synthesize.mutateAsync({ panelId: tfa.id }); } catch { /* non-fatal */ }
                  }
                  invalidateTfa();
                }}
                disabled={patchStage.isPending || synthesize.isPending || !tfa.allDeliberationsSubmitted}
                title={!tfa.allDeliberationsSubmitted ? 'All panelists must submit their deliberation first' : undefined}
              >
                {patchStage.isPending || synthesize.isPending
                  ? <><Loader2 className="h-3 w-3 animate-spin" /> Generating synthesis…</>
                  : <>Record Determination <ArrowRight className="h-3 w-3" /></>
                }
              </Button>
            </div>
          )}
          {!tfa.locked && !tfa.allDeliberationsSubmitted && (
            <p className="text-xs text-amber-700">All panelists must submit their deliberation before advancing to determination.</p>
          )}
        </div>
      )}

      {/* ── DETERMINATION ────────────────────────────────────── */}
      {tfa.stage === 'determination' && (
        <div className="space-y-4 border-t pt-4">
          {/* AI synthesis + compact inline panelist grid */}
          <TfaSynthesisView
            synthesis={tfa.aiDeterminationSynthesis ?? null}
            deliberations={deliberations}
          />
          {/* Allow regenerating synthesis in case it was generated before all panelists submitted */}
          {!tfa.locked && (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    await synthesize.mutateAsync({ panelId: tfa.id });
                    invalidateTfa();
                  } catch { /* non-fatal */ }
                }}
                disabled={synthesize.isPending}
              >
                {synthesize.isPending ? <><Loader2 className="h-3 w-3 animate-spin" /> Regenerating…</> : <>↺ Regenerate synthesis</>}
              </Button>
            </div>
          )}

          {/* Existing determination record (once saved) */}
          {tfa.determination && (
            <div
              className={`rounded-lg border p-3 ${
                tfa.determination.determination === 'uncertain' || tfa.determination.determination === 'partially_uncertain'
                  ? 'border-emerald-200 bg-emerald-50'
                  : 'border-destructive/40 bg-destructive/10'
              }`}
            >
              <p className="text-sm font-semibold">
                {tfa.determination.determination === 'uncertain' && '✓ Uncertain — R&D eligible'}
                {tfa.determination.determination === 'partially_uncertain' && '✓ Partially uncertain — some activities eligible'}
                {tfa.determination.determination === 'certain' && '✗ Certain — ineligible for R&D'}
              </p>
              {tfa.determination.notes && (
                <p className="text-xs text-muted-foreground mt-1">{tfa.determination.notes}</p>
              )}
            </div>
          )}

          {/* Final approver: compact confirm & lock card */}
          {!tfa.locked && iAmFinalApprover && !tfa.determination && (
            <div className="rounded-lg border p-4 space-y-3">
              {/* Recommended determination banner */}
              {(() => {
                const suggested = suggestDetermination(tfa.aiDeterminationSynthesis);
                const bannerClass =
                  suggested === 'uncertain'
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : suggested === 'partially_uncertain'
                    ? 'bg-amber-50 border-amber-200 text-amber-800'
                    : 'bg-destructive/10 border-destructive/30 text-destructive';
                const label =
                  suggested === 'uncertain'
                    ? 'Uncertain'
                    : suggested === 'partially_uncertain'
                    ? 'Partially Uncertain'
                    : 'Certain';
                return (
                  <div className={`rounded border px-3 py-2 text-sm ${bannerClass}`}>
                    Recommended determination: <strong>{label}</strong>
                  </div>
                );
              })()}

              <form
                onSubmit={determinationForm.handleSubmit(async (d) => {
                  await postDetermination.mutateAsync({ id: tfa.id, determination: d.determination, notes: d.notes });
                  // Always invalidate after determination — auto-lock may have fired
                  invalidateTfa();
                  // Attempt explicit lock; if TFA already auto-locked (409) treat as success
                  postLock.mutate({ id: tfa.id }, {
                    onSuccess: () => flashAndInvalidate('TFA locked and finalised'),
                    onError: (err: unknown) => {
                      const status = (err as { response?: { status?: number } })?.response?.status;
                      if (status === 409) {
                        // Already locked (auto-lock succeeded) — just show success
                        flashAndInvalidate('TFA locked and finalised');
                      }
                    },
                  });
                })}
                className="space-y-3"
              >
                {/* Compact radio pills */}
                <div className="flex gap-2 flex-wrap">
                  {[
                    { value: 'uncertain', label: 'Uncertain', color: 'border-emerald-300 bg-emerald-50 text-emerald-800' },
                    { value: 'partially_uncertain', label: 'Partially uncertain', color: 'border-amber-300 bg-amber-50 text-amber-800' },
                    { value: 'certain', label: 'Certain (ineligible)', color: 'border-destructive/40 bg-destructive/10 text-destructive' },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2 rounded-full border px-3 py-1 cursor-pointer text-sm transition-all ${
                        determinationForm.watch('determination') === opt.value
                          ? opt.color + ' font-medium'
                          : 'border-border hover:bg-accent/50'
                      }`}
                    >
                      <input
                        type="radio"
                        value={opt.value}
                        {...determinationForm.register('determination', { required: true })}
                        className="sr-only"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>

                <Button
                  type="submit"
                  disabled={postDetermination.isPending || postLock.isPending}
                  className="w-full"
                >
                  <Lock className="h-3 w-3" />
                  {postDetermination.isPending || postLock.isPending ? 'Locking…' : 'Confirm & Lock TFA'}
                </Button>
              </form>
            </div>
          )}

          {!tfa.locked && !iAmFinalApprover && (
            <p className="text-sm text-muted-foreground italic">
              Waiting for{' '}
              {tfa.panelists?.find((p) => p.isFinalApprover)?.name ??
                'the designated final approver'}{' '}
              to confirm the determination and lock the TFA.
            </p>
          )}

          {!tfa.locked && (
            <div className="flex items-center pt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => prevStage && patchStage.mutate({ id: tfa.id, stage: prevStage.key }, { onSuccess: invalidateTfa })}
                disabled={patchStage.isPending}
              >
                ← Back to Panel Deliberation
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Locked summary */}
      {tfa.locked && (
        <div className="border-t pt-4 mt-2 space-y-2 text-sm text-muted-foreground">
          <p><strong className="text-foreground">Problem statement:</strong> {tfa.problemStatement ?? '—'}</p>
          {tfa.aiDraftStatus === 'complete' && tfa.aiDraftProblemStatement && !tfa.problemStatement && (
            <details className="ml-4 mt-1">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                View AI draft
              </summary>
              <pre className="text-xs whitespace-pre-wrap text-muted-foreground mt-1 pl-3 border-l">
                {tfa.aiDraftProblemStatement}
              </pre>
            </details>
          )}
          <p><strong className="text-foreground">COTS notes:</strong> {tfa.cotsSweepNotes ?? '—'}</p>
          {tfa.aiDraftStatus === 'complete' && tfa.aiDraftCotsSweepNotes && !tfa.cotsSweepNotes && (
            <details className="ml-4 mt-1">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                View AI draft
              </summary>
              <pre className="text-xs whitespace-pre-wrap text-muted-foreground mt-1 pl-3 border-l">
                {tfa.aiDraftCotsSweepNotes}
              </pre>
            </details>
          )}
          <p><strong className="text-foreground">Panel:</strong> {tfa.panelists.length > 0 ? tfa.panelists.map((p) => userMap.get(p.userId ?? '') ?? p.userId ?? 'Unknown').join(', ') : '—'}</p>
          {user?.role === 'tenant_admin' && (
            <div className="border-t pt-3 mt-3">
              <Button
                variant="secondary"
                size="sm"
                className="text-destructive border-destructive/40 hover:bg-destructive/10"
                onClick={() => {
                  if (!confirm('Re-open this TFA? This will be recorded in the audit log.')) return;
                  postUnlock.mutate(tfa.id, { onSuccess: () => flashAndInvalidate('TFA re-opened') });
                }}
                disabled={postUnlock.isPending}
              >
                Re-open TFA (Admin)
              </Button>
              <p className="text-xs text-muted-foreground mt-1">Admin only — recorded in audit log.</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

interface SuggestedActivity {
  id: string;
  theme: string;
  tfaAspect?: string | null;
  sessionCount: number;
  firstSeen: string;
  lastSeen: string;
  status: string;
  supportingEvidence?: string[];
}

const IP_OWNERSHIP_OPTIONS = [
  { value: 'company', label: 'Company' },
  { value: 'client', label: 'Client' },
  { value: 'joint', label: 'Joint' },
  { value: 'licensed_back', label: 'Licensed Back' },
  { value: 'undetermined', label: 'Undetermined' },
] as const;

type IpOwnership = 'company' | 'client' | 'joint' | 'licensed_back' | 'undetermined';

interface ConfirmFormState {
  activityName: string;
  ipOwnership: IpOwnership;
}

function SuggestionCard({
  suggestion,
  onDismiss,
  onConfirm,
  dismissing,
  confirming,
}: {
  suggestion: SuggestedActivity;
  onDismiss: (id: string) => void;
  onConfirm: (id: string, form: ConfirmFormState) => void;
  dismissing: boolean;
  confirming: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState<ConfirmFormState>({
    activityName: suggestion.theme,
    ipOwnership: 'company',
  });

  return (
    <div className="rounded-lg border bg-accent/5 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{suggestion.theme}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {suggestion.sessionCount} evidence item{suggestion.sessionCount !== 1 ? 's' : ''} detected
            {suggestion.tfaAspect && <> · <span className="italic">{suggestion.tfaAspect}</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="accent"
            onClick={() => setExpanded((e) => !e)}
            disabled={confirming}
          >
            <CheckCircle2 className="h-3 w-3" />
            {expanded ? 'Cancel' : 'Confirm'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDismiss(suggestion.id)}
            disabled={dismissing || confirming}
            aria-label="Dismiss suggestion"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t pt-3 space-y-3">
          <p className="text-xs text-muted-foreground">Review and confirm activity details before creating:</p>
          <div className="space-y-2">
            <div>
              <label className="text-xs font-medium" htmlFor={`name-${suggestion.id}`}>Activity name</label>
              <input
                id={`name-${suggestion.id}`}
                type="text"
                value={form.activityName}
                onChange={(e) => setForm((f) => ({ ...f, activityName: e.target.value }))}
                className="mt-1 w-full rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                maxLength={255}
              />
            </div>
            <div>
              <label className="text-xs font-medium" htmlFor={`ip-${suggestion.id}`}>IP ownership</label>
              <select
                id={`ip-${suggestion.id}`}
                value={form.ipOwnership}
                onChange={(e) => setForm((f) => ({ ...f, ipOwnership: e.target.value as IpOwnership }))}
                className="mt-1 w-full rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {IP_OWNERSHIP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setExpanded(false)}>Cancel</Button>
            <Button
              size="sm"
              onClick={() => onConfirm(suggestion.id, form)}
              disabled={!form.activityName.trim() || confirming}
            >
              {confirming ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              Create activity
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActivitiesSection({ projectId }: { projectId: string }) {
  const { data, isLoading, refetch: refetchActivities } = useGetProjectActivities(projectId);
  const items = data?.data?.items ?? [];

  // Fetch suggested activities
  const [suggestions, setSuggestions] = useState<SuggestedActivity[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [clustering, setClustering] = useState(false);
  const [clusterError, setClusterError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<{ activitiesCreated: number; artefactsQueuedForTagging: boolean } | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [deletingActivity, setDeletingActivity] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteActivity = async (activityId: string) => {
    setDeletingActivity(activityId);
    setDeleteError(null);
    try {
      await customInstance({ url: `/api/activities/${activityId}`, method: 'DELETE' });
      void refetchActivities();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
        ?? 'Failed to delete activity. It may have approved time entries.';
      setDeleteError(msg);
    } finally {
      setDeletingActivity(null);
    }
  };

  useEffect(() => {
    customInstance<{ data: { items: SuggestedActivity[] } }>({
      url: `/api/projects/${projectId}/suggested-activities`,
      method: 'GET',
    }).then((res) => setSuggestions(res.data.items ?? []))
      .catch(() => {})
      .finally(() => setSuggestionsLoading(false));
  }, [projectId]);

  const dismissSuggestion = async (suggestionId: string) => {
    setDismissing(suggestionId);
    try {
      await customInstance({
        url: `/api/projects/${projectId}/suggested-activities/${suggestionId}/dismiss`,
        method: 'POST',
      });
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestionId));
    } finally {
      setDismissing(null);
    }
  };

  const handleAnalyseMyWork = async () => {
    setClustering(true);
    setClusterError(null);
    try {
      const res = await customInstance<{ data: { clustersFound: number; suggestions: Array<{ id: string; theme: string }> } }>({
        url: `/api/projects/${projectId}/suggested-activities/cluster`,
        method: 'POST',
      });
      const newCount = res.data.clustersFound;
      if (newCount > 0) {
        // Refresh suggestions list
        const updated = await customInstance<{ data: { items: SuggestedActivity[] } }>({
          url: `/api/projects/${projectId}/suggested-activities`,
          method: 'GET',
        });
        setSuggestions(updated.data.items ?? []);
      } else {
        setClusterError('No new activity themes detected from your current evidence.');
      }
    } catch {
      setClusterError('Failed to analyse work. Please try again.');
    } finally {
      setClustering(false);
    }
  };

  const confirmSuggestion = async (suggestionId: string, form: ConfirmFormState) => {
    setConfirming(suggestionId);
    try {
      await customInstance({
        url: `/api/projects/${projectId}/suggested-activities/${suggestionId}/confirm`,
        method: 'POST',
        data: {
          activityName: form.activityName,
          ipOwnership: form.ipOwnership,
        },
      });
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestionId));
      void refetchActivities();
    } finally {
      setConfirming(null);
    }
  };

  const handleGenerateFromPrd = async () => {
    setGenerating(true);
    setGenerateError(null);
    setGenerateResult(null);
    try {
      const res = await customInstance<{ data: { activitiesCreated: number; artefactsQueuedForTagging: boolean } }>({
        url: `/api/projects/${projectId}/generate-activities`,
        method: 'POST',
      });
      setGenerateResult(res.data);
      void refetchActivities();
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { error?: { code?: string; message?: string } } } })?.response?.data?.error?.code;
      if (code === 'NO_PRD') {
        setGenerateError('No PRD document found for this project. Upload a PRD artefact first.');
      } else {
        setGenerateError('Failed to re-generate activities. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* AI analysis triggers */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="font-medium flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" aria-hidden="true" />
            AI Activity Generation
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Activities are generated automatically on PRD upload. Re-generate manually or detect themes from existing evidence.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void handleGenerateFromPrd()}
            disabled={generating}
            title="Re-generate R&D activities from the current PRD document"
          >
            {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
            {generating ? 'Re-generating…' : 'Re-generate from PRD'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void handleAnalyseMyWork()}
            disabled={clustering}
          >
            {clustering ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {clustering ? 'Analysing…' : 'Analyse my work'}
          </Button>
        </div>
      </div>

      {generateError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{generateError}</span>
          <button onClick={() => setGenerateError(null)} className="ml-auto text-destructive/60 hover:text-destructive" aria-label="Dismiss">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {generateResult && generateResult.activitiesCreated > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Re-generated {generateResult.activitiesCreated} {generateResult.activitiesCreated === 1 ? 'activity' : 'activities'} from PRD.
            {generateResult.artefactsQueuedForTagging && ' Evidence retroactive tagging queued.'}
          </span>
          <button onClick={() => setGenerateResult(null)} className="ml-auto text-emerald-600/60 hover:text-emerald-600 dark:text-emerald-400/60" aria-label="Dismiss">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {generateResult && generateResult.activitiesCreated === 0 && (
        <p className="text-sm text-muted-foreground">
          No new activities re-generated — all PRD-derived activities already exist.
        </p>
      )}

      {clusterError && (
        <p className="text-sm text-destructive">{clusterError}</p>
      )}

      {/* Suggested activities from AI clustering */}
      {!suggestionsLoading && suggestions.length > 0 && (() => {
        // Filter out suggestions whose theme already matches a confirmed activity
        const confirmedNames = new Set(items.map((a) => a.name.trim().toLowerCase()));
        const unmatched = suggestions.filter((s) => !confirmedNames.has(s.theme.trim().toLowerCase()));
        if (unmatched.length === 0) return null;
        return (
          <div>
            <p className="text-xs text-muted-foreground mb-3">
              AI-detected recurring work themes. Confirm to create as a formal activity, or dismiss.
            </p>
            <div className="space-y-2">
              {(showAllSuggestions ? unmatched : unmatched.slice(0, 5)).map((s) => (
                <SuggestionCard
                  key={s.id}
                  suggestion={s}
                  onDismiss={(id) => void dismissSuggestion(id)}
                  onConfirm={(id, form) => void confirmSuggestion(id, form)}
                  dismissing={dismissing === s.id}
                  confirming={confirming === s.id}
                />
              ))}
            </div>
            {unmatched.length > 5 && (
              <button
                className="mt-2 text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                onClick={() => setShowAllSuggestions(prev => !prev)}
              >
                {showAllSuggestions ? 'Show fewer suggestions' : `Show ${unmatched.length - 5} more suggestions`}
              </button>
            )}
          </div>
        );
      })()}

      {/* Delete error */}
      {deleteError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="ml-auto text-destructive/60 hover:text-destructive" aria-label="Dismiss">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Confirmed activities */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium">R&D Activities</h3>
          <Link href={`/projects/${projectId}/activities/new`}>
            <Button size="sm">
              <Plus className="h-3 w-3" />
              Add activity
            </Button>
          </Link>
        </div>

        {isLoading && <div className="h-12 rounded-lg skeleton" />}

        {!isLoading && items.length === 0 && suggestions.length === 0 && (
          <p className="text-sm text-muted-foreground">No activities yet. Connect a signal source and backfill to get AI-suggested activities.</p>
        )}

        {!isLoading && items.length === 0 && suggestions.length > 0 && (
          <p className="text-sm text-muted-foreground">No confirmed activities yet. Review the suggestions above.</p>
        )}

        {!isLoading && items.length > 0 && (() => {
          // Deduplicate by id, then group by normalised name
          const unique = Array.from(new Map(items.map((a) => [a.id, a])).values());
          const groupMap = unique.reduce<Map<string, typeof unique>>((acc, a) => {
            const key = a.name.trim().toLowerCase();
            const group = acc.get(key) ?? [];
            group.push(a);
            acc.set(key, group);
            return acc;
          }, new Map());
          const groups = Array.from(groupMap.values());
          return (
            <ul className="space-y-2">
              {groups.map((group) => {
                const a = group[0]!;
                return (
                  <li key={a.id} className="flex items-center gap-2">
                    <Link href={`/activities/${a.id}`} className="flex-1 min-w-0">
                      <Card variant="interactive" className="px-4 py-3">
                        <div className="flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <span className="flex items-center gap-2 text-sm font-medium">
                              {a.name}
                              {group.length > 1 && (
                                <span className="text-xs text-muted-foreground font-normal">×{group.length}</span>
                              )}
                            </span>
                            {((a as { entryCount?: number }).entryCount ?? 0) > 0 && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {(a as { rdHours?: number }).rdHours?.toFixed(1) ?? '0'} R&D hrs
                                <span className="mx-1">·</span>
                                {(a as { entryCount?: number }).entryCount} entr{(a as { entryCount?: number }).entryCount === 1 ? 'y' : 'ies'}
                              </p>
                            )}
                          </div>
                          <Badge
                            variant={
                              a.ipOwnership === 'client'
                                ? 'warning'
                                : a.ipOwnership === 'undetermined'
                                ? 'warning'
                                : 'default'
                            }
                          >
                            {a.ipOwnership === 'client' ? 'CLIENT IP' : formatLabel(a.ipOwnership)}
                          </Badge>
                        </div>
                      </Card>
                    </Link>
                    <button
                      onClick={() => void handleDeleteActivity(a.id)}
                      disabled={deletingActivity === a.id}
                      className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                      title="Delete activity"
                      aria-label={`Delete activity ${a.name}`}
                    >
                      {deletingActivity === a.id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                        : <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          );
        })()}
      </div>
    </div>
  );
}

type ClassificationResult = {
  label: string;
  confidence: string;
  rdPercentage: string;
  rationale: string | null;
  rationaleSlim?: string | null;
};

interface SignalSourceStats {
  id: string;
  repoOwner: string;
  repoName: string;
  firstCommitAt: string | null;
  lastCommitAt: string | null;
  contributorCount: number;
}

function SignalsTab({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const { data: reposData, isLoading: reposLoading, error: reposError, refetch: refetchRepos } = useGetProjectRepos(projectId);
  const { data: availableData, isLoading: availableLoading } = useGetSignalAvailableRepos();
  const addRepo = useAddProjectRepo();
  const removeRepo = useRemoveProjectRepo();
  const [adding, setAdding] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [repoSearch, setRepoSearch] = useState('');
  const [backfillingId, setBackfillingId] = useState<string | null>(null);

  // Fetch signal source stats to show firstCommitAt, contributorCount, backfill button
  const { data: signalsData } = useQuery({
    queryKey: ['signals-rich'],
    queryFn: () =>
      customInstance<{ data: { items: SignalSourceStats[] } }>({ url: '/api/signals', method: 'GET' })
        .then((r) => r.data.items),
  });
  const signalsByRepo = useMemo(() => {
    const m = new Map<string, SignalSourceStats>();
    for (const s of signalsData ?? []) {
      m.set(`${s.repoOwner}/${s.repoName}`.toLowerCase(), s);
    }
    return m;
  }, [signalsData]);

  const backfillMutation = useMutation({
    mutationFn: (signalId: string) =>
      customInstance({ url: `/api/signals/${signalId}/backfill`, method: 'POST', data: {} }),
    onSettled: () => setBackfillingId(null),
  });
  const repos = reposData?.data?.items ?? [];
  const availableItems = availableData?.data?.items ?? [];
  const scopedKeys = new Set(repos.map((r) => `${r.repoOwner}/${r.repoName}`.toLowerCase()));
  const available = useMemo(() => {
    const unlinked = availableItems.filter((item) => !scopedKeys.has(item.full_name.toLowerCase()));
    const q = repoSearch.trim().toLowerCase();
    if (!q) return unlinked;
    return unlinked.filter((r) => r.full_name.toLowerCase().includes(q));
  }, [availableItems, scopedKeys, repoSearch]);

  const handleAdd = () => {
    if (!selectedRepo) return;
    const [repoOwner = '', repoName = ''] = selectedRepo.split('/');
    addRepo.mutate(
      { projectId, repoOwner, repoName },
      {
        onSuccess: () => {
          setAdding(false);
          setSelectedRepo('');
          void refetchRepos();
          void queryClient.invalidateQueries({ queryKey: ['/api/signals/available-repos'] });
        },
      },
    );
  };

  const handleRemove = (repoId: string) => {
    removeRepo.mutate(
      { projectId, repoId },
      {
        onSuccess: () => {
          void refetchRepos();
          void queryClient.invalidateQueries({ queryKey: ['/api/signals/available-repos'] });
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium">Signal repos</h2>
        {!adding && available.length > 0 && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-accent"
          >
            <Plus className="h-3 w-3" aria-hidden="true" />
            Connect repo
          </button>
        )}
      </div>

      {adding && (
        <div className="space-y-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <input
              type="search"
              value={repoSearch}
              onChange={(e) => setRepoSearch(e.target.value)}
              className="w-full rounded-lg border bg-background pl-9 pr-3 py-2 text-sm"
              placeholder="Search repos…"
              aria-label="Search available repos"
              autoFocus
            />
          </div>
          {/* Repo list */}
          <div className="rounded-lg border overflow-y-auto max-h-52">
            {availableLoading && (
              <div className="px-4 py-5 text-sm text-muted-foreground text-center">Loading repos…</div>
            )}
            {!availableLoading && available.length === 0 && (
              <div className="px-4 py-5 text-sm text-muted-foreground text-center">
                {availableItems.length === 0 ? 'No repos available.' : 'No repos match your search.'}
              </div>
            )}
            {!availableLoading && available.map((item) => (
              <button
                key={item.full_name}
                type="button"
                onClick={() => setSelectedRepo(selectedRepo === item.full_name ? '' : item.full_name)}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-muted/50 transition-colors border-b last:border-b-0 ${
                  selectedRepo === item.full_name ? 'bg-primary/5' : ''
                }`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <GitBranch className={`h-3.5 w-3.5 shrink-0 ${selectedRepo === item.full_name ? 'text-primary' : 'text-muted-foreground'}`} aria-hidden="true" />
                  <span className="truncate font-medium">{item.full_name}</span>
                </span>
                {selectedRepo === item.full_name && (
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 ml-2" aria-hidden="true" />
                )}
              </button>
            ))}
          </div>
          {/* Actions */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAdd}
              disabled={!selectedRepo || addRepo.isPending}
              className="rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {addRepo.isPending ? 'Connecting…' : `Connect${selectedRepo ? ` ${selectedRepo.split('/')[1]}` : ''}`}
            </button>
            <button
              type="button"
              onClick={() => { setAdding(false); setSelectedRepo(''); setRepoSearch(''); }}
              className="rounded-lg border px-3 py-2 text-xs font-medium hover:bg-accent"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {addRepo.isError && <p className="text-xs text-destructive mt-1">Failed to connect repo. Please try again.</p>}

      {!!reposError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load signal repos.</span>
          <button
            type="button"
            onClick={() => void refetchRepos()}
            className="ml-auto inline-flex items-center gap-1 underline text-xs"
          >
            <RefreshCw className="h-3 w-3" aria-hidden="true" />
            Retry
          </button>
        </div>
      )}

      {reposLoading && (
        <div className="space-y-2">
          <div className="h-10 rounded-lg skeleton" />
          <div className="h-10 rounded-lg skeleton" />
        </div>
      )}

      {!reposLoading && !reposError && repos.length === 0 && (
        <div className="rounded-lg border border-dashed px-4 py-8 text-center">
          <GitBranch className="mx-auto h-8 w-8 text-muted-foreground/40" aria-hidden="true" />
          <p className="mt-3 text-sm text-muted-foreground">No repos connected to this project yet.</p>
          {available.length > 0 ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-accent"
            >
              <Plus className="h-3 w-3" aria-hidden="true" />
              Connect repo
            </button>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              Install the GitHub App and connect a repo on the{' '}
              <Link href="/signals" className="underline">
                Signal Sources
              </Link>{' '}
              page first.
            </p>
          )}
        </div>
      )}

      {!reposLoading && repos.length > 0 && (
        <ul className="space-y-2">
          {repos.map((r) => {
            const sig = signalsByRepo.get(`${r.repoOwner}/${r.repoName}`.toLowerCase());
            const isBackfilling = backfillingId === sig?.id;
            return (
              <li key={r.id} className="rounded-lg border px-3 py-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <GitBranch className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
                    <span className="text-sm font-mono truncate">
                      {r.repoOwner}/{r.repoName}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-3">
                    {r.hasPrd ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-600" title="PRD.md found">
                        <FileText className="h-3 w-3" aria-hidden="true" />
                        PRD
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/50" title="No PRD.md">No PRD</span>
                    )}
                    {sig && (
                      <button
                        type="button"
                        onClick={() => {
                          setBackfillingId(sig.id);
                          backfillMutation.mutate(sig.id);
                        }}
                        disabled={isBackfilling}
                        className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                        aria-label={`Backfill ${r.repoOwner}/${r.repoName}`}
                        title="Re-backfill this repo"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${isBackfilling ? 'animate-spin' : ''}`} aria-hidden="true" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemove(r.id)}
                      disabled={removeRepo.isPending}
                      className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                      aria-label={`Disconnect ${r.repoOwner}/${r.repoName} from project`}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                {sig && (
                  <div className="mt-1.5 flex items-center gap-4 text-xs text-muted-foreground pl-5">
                    {sig.firstCommitAt && (
                      <span>First commit: {new Date(sig.firstCommitAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })}</span>
                    )}
                    {sig.lastCommitAt && (
                      <span>Last: {new Date(sig.lastCommitAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })}</span>
                    )}
                    {sig.contributorCount > 0 && (
                      <span>{sig.contributorCount} contributor{sig.contributorCount !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {removeRepo.isError && <p className="text-xs text-destructive mt-1">Failed to disconnect repo. Please try again.</p>}

    </div>
  );
}

interface GrantMatch {
  id: string;
  grantId: string;
  matchScore: string;
  alignment: string | null;
  gaps: string | null;
  recommendation: string;
  matchedAt: string;
}

type ProjectArtefactItem = {
  id: string;
  title: string;
  type: string;
  createdAt: string;
  activityId: string | null;
  activityTagSource: 'ai' | 'manual' | null;
  activityTagConfidence: string | null;
  projectId: string | null;
};

type ProjectArtefactsResponse = {
  byActivity: Record<string, ProjectArtefactItem[]>;
  untagged: ProjectArtefactItem[];
  total: number;
  limit: number;
  offset: number;
};

function ProjectEvidenceSection({ projectId }: { projectId: string }) {
  const activitiesQuery = useGetProjectActivities(projectId);
  const activities: Activity[] = activitiesQuery.data?.data?.items ?? [];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['projectArtefacts', projectId],
    queryFn: () =>
      customInstance<{ data: ProjectArtefactsResponse }>({
        url: `/api/projects/${projectId}/artefacts`,
        method: 'GET',
      }),
    enabled: !!projectId,
  });

  const artefactsData = data?.data;

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="h-6 w-40 rounded-lg skeleton" />
        <div className="h-16 w-full rounded-lg skeleton" />
        <div className="h-16 w-full rounded-lg skeleton" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load evidence artefacts.</span>
        </div>
        <button
          onClick={() => void refetch()}
          className="text-xs border rounded px-2 py-1 hover:bg-muted"
        >
          <RefreshCw className="h-3 w-3 inline mr-1" aria-hidden="true" />
          Retry
        </button>
      </div>
    );
  }

  const byActivity = artefactsData?.byActivity ?? {};
  const untagged = artefactsData?.untagged ?? [];
  const total = artefactsData?.total ?? 0;

  const activityMap = new Map(activities.map((a) => [a.id, a]));

  const hasAny = total > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium flex items-center gap-2">
          <FileText className="h-4 w-4" aria-hidden="true" />
          Evidence
          {hasAny && (
            <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
        </h3>
        <Link
          href={`/artefacts/new?projectId=${projectId}`}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          Add artefact
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </div>

      {!hasAny && (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <FileText className="h-8 w-8 mx-auto text-muted-foreground/40" aria-hidden="true" />
          <p className="mt-3 text-sm text-muted-foreground">No artefacts yet.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Create artefacts from the Evidence Vault or connect a GitHub signal source.
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Link
              href={`/artefacts/new?projectId=${projectId}`}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-3 w-3" />
              Add artefact
            </Link>
            <Link
              href="/signals"
              className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium hover:bg-accent"
            >
              Connect signal source
            </Link>
          </div>
        </div>
      )}

      {hasAny && (
        <div className="space-y-3">
          {Object.entries(byActivity).map(([activityId, artefacts]) => {
            const activity = activityMap.get(activityId);
            return (
              <div key={activityId} className="rounded-lg border p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Tag className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                    {activity ? (
                      <Link href={`/activities/${activityId}`} className="text-sm font-medium hover:underline">
                        {activity.name}
                      </Link>
                    ) : (
                      <span className="text-sm font-medium font-mono text-muted-foreground">{activityId}</span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">{artefacts.length} artefact{artefacts.length !== 1 ? 's' : ''}</span>
                </div>
                <ul className="space-y-2">
                  {artefacts.map((artefact) => (
                    <li key={artefact.id}>
                      <Link href={`/artefacts/${artefact.id}`}>
                        <div className="flex items-center justify-between rounded-lg px-2 py-2 hover:bg-accent transition-colors cursor-pointer">
                          <span className="text-sm truncate">{artefact.title}</span>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                              {formatLabel(artefact.type)}
                            </span>
                            {artefact.activityTagSource === 'ai' && (
                              <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-700 font-medium">
                                AI
                                {artefact.activityTagConfidence !== null && artefact.activityTagConfidence !== undefined
                                  ? ` ${parseFloat(artefact.activityTagConfidence).toFixed(0)}%`
                                  : ''}
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {new Date(artefact.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {untagged.length > 0 && (
            <div className="rounded-lg border border-dashed p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Tag className="h-3.5 w-3.5 text-muted-foreground/50" aria-hidden="true" />
                  <span className="text-sm font-medium text-muted-foreground">Untagged</span>
                </div>
                <span className="text-xs text-muted-foreground">{untagged.length} artefact{untagged.length !== 1 ? 's' : ''}</span>
              </div>
              <ul className="space-y-2">
                {untagged.map((artefact) => (
                  <li key={artefact.id}>
                    <Link href={`/artefacts/${artefact.id}`}>
                      <div className="flex items-center justify-between rounded-lg px-2 py-2 hover:bg-accent transition-colors cursor-pointer">
                        <span className="text-sm truncate">{artefact.title}</span>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                            {formatLabel(artefact.type)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(artefact.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// TFA Progress Checklist — 7-item readiness indicator
// ─────────────────────────────────────────────────────────
function TfaProgressChecklist({ tfa }: { tfa: TfaPanelFull }) {
  const items = [
    {
      label: 'Problem statement drafted',
      done: !!(tfa.problemStatement || tfa.aiDraftProblemStatement),
    },
    {
      label: 'COTS sweep completed',
      done: !!(tfa.cotsSweepNotes || tfa.aiDraftCotsSweepNotes),
    },
    {
      label: 'Panel assembled with final approver',
      done: tfa.panelists.length > 0 && tfa.panelists.some((p) => p.isFinalApprover),
    },
    {
      label: 'All panelists confirmed',
      done: tfa.panelists.length > 0 && tfa.panelists.every((p) => p.confirmedAt != null),
    },
    {
      label: 'All deliberations submitted',
      done: tfa.allDeliberationsSubmitted === true,
    },
    {
      label: 'Determination recorded',
      done: !!tfa.determination,
    },
    {
      label: 'Panel locked',
      done: tfa.locked,
    },
  ];

  const doneCount = items.filter((i) => i.done).length;

  return (
    <div className="rounded-lg border border-hairline bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">TFA Checklist</h3>
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            doneCount === 7
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          {doneCount} of 7
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-[13px]">
            {item.done ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" aria-hidden="true" />
            ) : (
              <div
                className="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground/30 shrink-0"
                aria-hidden="true"
              />
            )}
            <span className={item.done ? 'text-foreground' : 'text-muted-foreground'}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// PRD Viewer Card — latest PRD artefact linked to project
// ─────────────────────────────────────────────────────────
function PrdViewerCard({ projectId }: { projectId: string }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['project-prd', projectId],
    queryFn: async () => {
      const res = await customInstance<{
        data: { items: Array<{ id: string; title: string; content: string; createdAt: string }> };
      }>({
        url: `/api/artefacts?projectId=${projectId}&type=prd_document&limit=1`,
        method: 'GET',
      });
      return res.data.items[0] ?? null;
    },
  });

  if (isLoading) return <div className="h-14 rounded-lg skeleton" />;
  if (!data) return null;

  return (
    <>
      <button
        onClick={() => setPanelOpen(true)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-hairline bg-card text-left hover:bg-accent/40 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-medium truncate">{data.title}</span>
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium shrink-0">PRD</span>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 ml-2" aria-hidden="true" />
      </button>

      <SlidePanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={data.title}
        width="w-[680px]"
      >
        <div className="px-6 py-5">
          <div className="flex items-center gap-2 mb-5 text-xs text-muted-foreground">
            <span className="px-1.5 py-0.5 rounded bg-muted font-medium">PRD</span>
            <span>·</span>
            <span>{new Date(data.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>
          <MarkdownContent>{data.content}</MarkdownContent>
        </div>
      </SlidePanel>
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Activities Preview — first 3 activities with IP badges
// ─────────────────────────────────────────────────────────
function ActivitiesPreview({ projectId }: { projectId: string }) {
  const { data, isLoading } = useGetProjectActivities(projectId);
  const activities = (data?.data?.items ?? []) as Activity[];

  if (isLoading) return <div className="h-16 rounded-lg skeleton" />;
  if (activities.length === 0) return null;

  const preview = activities.slice(0, 3);

  const ipBadge = (ip: string) => {
    if (ip === 'client')
      return (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium shrink-0">
          Client IP
        </span>
      );
    if (ip === 'undetermined')
      return (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium shrink-0">
          IP TBD
        </span>
      );
    return null;
  };

  return (
    <div className="rounded-lg border border-hairline bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Activities</h3>
        <Link href={`/projects/${projectId}`} className="text-xs text-primary hover:underline">
          View all →
        </Link>
      </div>
      <div className="space-y-1">
        {preview.map((a) => (
          <div key={a.id} className="flex items-center gap-2 py-0.5 text-[13px]">
            <span className="truncate flex-1 text-foreground">{a.name}</span>
            {ipBadge(a.ipOwnership)}
          </div>
        ))}
        {activities.length > 3 && (
          <p className="text-xs text-muted-foreground pt-0.5">+{activities.length - 3} more</p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Overview tab
// ─────────────────────────────────────────────────────────
function QuickActions({ projectId: _projectId, onFlash, onNavigateTab: _onNavigateTab }: { projectId: string; onFlash: (msg: string) => void; onNavigateTab: (tab: string) => void }) {
  const [suggesting, setSuggesting] = useState(false);

  const triggerActivitySuggestions = async () => {
    setSuggesting(true);
    try {
      await customInstance({
        url: `/api/signals/backfill`,
        method: 'POST',
        data: {},
      });
      onFlash('Activity suggestion triggered — check Activities tab in a moment');
    } catch {
      onFlash('Failed to trigger activity suggestions');
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div className="rounded-xl border bg-muted/20 p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Quick Actions</h3>
      <div className="flex flex-wrap gap-3">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void triggerActivitySuggestions()}
          disabled={suggesting}
        >
          <Zap className={`h-3.5 w-3.5 ${suggesting ? 'animate-pulse' : ''}`} aria-hidden="true" />
          {suggesting ? 'Generating...' : 'Suggest activities'}
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Product Group inline edit (overview tab)
// ─────────────────────────────────────────────────────────
function ProductGroupField({
  project,
  onSaveFlash,
}: {
  project: Project & { tfaPanelId?: string | null; productGroup?: string | null };
  onSaveFlash: (msg: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(project.productGroup ?? '');
  const updateProject = useUpdateProject();
  const queryClient = useQueryClient();

  function handleSave() {
    const trimmed = value.trim() || null;
    updateProject.mutate(
      { id: project.id, data: { productGroup: trimmed } as Parameters<typeof updateProject.mutate>[0]['data'] },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey(project.id) });
          queryClient.invalidateQueries({ queryKey: getGetProjectsQueryKey() });
          onSaveFlash('Product group saved');
          setEditing(false);
        },
      },
    );
  }

  const current = project.productGroup?.trim();

  if (!editing) {
    return (
      <div className="flex items-center gap-2 text-[13px]">
        <Tag className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
        <span className="text-muted-foreground">Product group:</span>
        <span className={current ? 'text-foreground font-medium' : 'text-muted-foreground italic'}>
          {current ?? 'none'}
        </span>
        <button
          onClick={() => { setValue(project.productGroup ?? ''); setEditing(true); }}
          className="text-muted-foreground hover:text-foreground transition-colors ml-0.5"
          aria-label="Edit product group"
        >
          <Pencil className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Tag className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
      <span className="text-[13px] text-muted-foreground">Product group:</span>
      <input
        autoFocus
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
        placeholder="e.g. Platform, Integrations, AI"
        className="flex-1 text-[13px] bg-background border border-input rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-ring min-w-0"
        maxLength={255}
      />
      <button
        onClick={handleSave}
        disabled={updateProject.isPending}
        className="p-1 rounded text-positive hover:bg-positive/10 transition-colors"
        aria-label="Save product group"
      >
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <button
        onClick={() => setEditing(false)}
        className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Cancel"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Aliases inline field (codenames / alternative names)
// ─────────────────────────────────────────────────────────
function AliasesField({
  project,
  onSaveFlash,
}: {
  project: Project & { tfaPanelId?: string | null; aliases?: string | null };
  onSaveFlash: (msg: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(project.aliases ?? '');
  const updateProject = useUpdateProject();
  const queryClient = useQueryClient();

  function handleSave() {
    const trimmed = value.trim() || null;
    updateProject.mutate(
      { id: project.id, data: { aliases: trimmed } as Parameters<typeof updateProject.mutate>[0]['data'] },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey(project.id) });
          onSaveFlash('Aliases saved');
          setEditing(false);
        },
      },
    );
  }

  const current = project.aliases?.trim();

  if (!editing) {
    return (
      <div className="flex items-center gap-2 text-[13px]">
        <Tag className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
        <span className="text-muted-foreground">Aliases:</span>
        <span className={current ? 'text-foreground font-medium' : 'text-muted-foreground italic'}>
          {current ?? 'none'}
        </span>
        <button
          onClick={() => { setValue(project.aliases ?? ''); setEditing(true); }}
          className="text-muted-foreground hover:text-foreground transition-colors ml-0.5"
          aria-label="Edit aliases"
        >
          <Pencil className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Tag className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
      <span className="text-[13px] text-muted-foreground">Aliases:</span>
      <input
        autoFocus
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
        placeholder="e.g. Crystal Ball, CB"
        className="flex-1 text-[13px] bg-background border border-input rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-ring min-w-0"
        maxLength={500}
      />
      <button
        onClick={handleSave}
        disabled={updateProject.isPending}
        className="p-1 rounded text-positive hover:bg-positive/10 transition-colors"
        aria-label="Save aliases"
      >
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <button
        onClick={() => setEditing(false)}
        className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Cancel"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

// ERA-006: the R&D Attribution Mode toggle (granular vs project_level) was removed. project_level —
// "count 100% of a project's hours as R&D without per-entry classification" — was an un-evidenced
// override that only the dashboard honoured, diverging from the claim. R&D hours are now always the
// evidence-based classified rd%, consistently across the dashboard and the claim.

function OverviewTab({
  project,
  tfa,
  tfaLoading,
  tfaLocked,
  onCreateTfa,
  createTfaPending,
  onSaveFlash,
  onNavigateTab,
}: {
  project: Project & { tfaPanelId?: string | null };
  tfa: TfaPanelFull | null;
  tfaLoading: boolean;
  tfaLocked: boolean;
  onCreateTfa: () => void;
  createTfaPending: boolean;
  onSaveFlash: (msg: string) => void;
  onNavigateTab: (tab: string) => void;
}) {
  const { getToken } = useAuth();
  const { data: reposData } = useGetProjectRepos(project.id);
  const { data: usersData } = useGetUsers();
  const [grantMatches, setGrantMatches] = useState<GrantMatch[]>([]);
  const [grantsLoading, setGrantsLoading] = useState(true);

  const repos = reposData?.data?.items ?? [];
  const teamMembers = usersData?.data?.items ?? [];

  // totalHours (raw deduped worked hours) + rdHours (mode-aware capped R&D hours) come from the
  // backend shared engine (GET /api/projects/:id), so this page matches the projects list,
  // dashboard and claim exactly — and reflects ALL entries, not just the currently-loaded page.
  const totalHours = project.totalHours ?? 0;
  const rdHours = project.rdHours ?? 0;

  // Load grant matches
  const loadGrants = useCallback(async () => {
    setGrantsLoading(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000); // 3s timeout
    try {
      const token = await getToken();
      const res = await fetch(`/api/projects/${project.id}/grant-matches`, {
        headers: { Authorization: `Bearer ${token ?? ''}` },
        signal: controller.signal,
      });
      if (!res.ok) throw new Error();
      const json = (await res.json()) as { data: { items: GrantMatch[] } };
      setGrantMatches(json.data.items);
    } catch {
      // Silently fail — overview is informational
    } finally {
      clearTimeout(timeout);
      setGrantsLoading(false);
    }
  }, [project.id, getToken]);

  useEffect(() => {
    void loadGrants();
  }, [loadGrants]);

  const recIcon = (r: string) =>
    r === 'strong_fit' ? (
      <TrendingUp className="h-4 w-4 text-emerald-500" aria-hidden="true" />
    ) : r === 'partial_fit' ? (
      <Minus className="h-4 w-4 text-amber-500" aria-hidden="true" />
    ) : (
      <TrendingDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
    );

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="rounded-[12px] border border-border bg-card px-4 py-3.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground mb-2">R&amp;D Hours</p>
          <p className="font-mono text-xl font-semibold text-foreground">{rdHours.toFixed(1)}</p>
        </div>
        <div className="rounded-[12px] border border-border bg-card px-4 py-3.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground mb-2">Intensity</p>
          <p className="font-mono text-xl font-semibold" style={{ color: 'oklch(0.34 0.1 155)' }}>
            {totalHours > 0 ? `${Math.round((rdHours / totalHours) * 100)}%` : '—'}
          </p>
        </div>
        <div className="rounded-[12px] border border-border bg-card px-4 py-3.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground mb-2">Total Hours</p>
          <p className="font-mono text-xl font-semibold text-foreground">{totalHours.toFixed(1)}</p>
        </div>
        <div className="rounded-[12px] border border-border bg-card px-4 py-3.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground mb-2">Team</p>
          <p className="font-mono text-xl font-semibold text-foreground">{teamMembers.length}</p>
        </div>
        <div className="rounded-[12px] border border-border bg-card px-4 py-3.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground mb-2">Repos</p>
          <p className="font-mono text-xl font-semibold text-foreground">{repos.length}</p>
        </div>
      </div>

      {/* Eligibility now — 3-panel card */}
      {project.status !== 'no_rd' && !tfaLoading && (
        <div
          className="rounded-[14px] border-l-[3px] border border-border bg-card overflow-hidden"
          style={{ borderLeftColor: tfaLocked ? 'oklch(0.55 0.14 155)' : 'oklch(0.65 0.14 65)' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
            {/* Panel 1: Current status */}
            <div className="px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground mb-3 flex items-center gap-1.5">
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M12 8v5l3 2"/><circle cx="12" cy="12" r="9"/>
                </svg>
                Eligibility now
              </p>
              {tfa ? (
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: tfa.locked ? 'oklch(0.95 0.03 155)' : 'oklch(0.96 0.03 65)' }}
                  >
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={tfa.locked ? 'oklch(0.40 0.11 155)' : 'oklch(0.50 0.11 65)'}
                      strokeWidth="2.2"
                      aria-hidden="true"
                    >
                      {tfa.locked ? (
                        <>
                          <path d="M12 3l7 3v5c0 4.5-3 8.3-7 9.5C8 19.3 5 15.5 5 11V6z"/>
                          <path d="M9.2 12l2 2 3.6-4"/>
                        </>
                      ) : (
                        <path d="M12 3l7 3v5c0 4.5-3 8.3-7 9.5C8 19.3 5 15.5 5 11V6z"/>
                      )}
                    </svg>
                  </div>
                  <span
                    className="text-lg font-semibold"
                    style={{ color: tfa.locked ? 'oklch(0.34 0.1 155)' : 'oklch(0.50 0.11 65)' }}
                  >
                    {tfa.locked ? 'Intact' : 'TFA In Progress'}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">No TFA panel yet</span>
              )}
              <p className="text-[11px] text-muted-foreground mt-2">Continuously monitored · advisory only</p>
            </div>

            {/* Panel 2: FY trajectory — 12 bars (Jul–Jun) */}
            <div className="px-5 py-4">
              <p className="text-xs font-semibold text-foreground mb-3">
                {project.incomeYear ? `${project.incomeYear} trajectory` : 'FY trajectory'}
              </p>
              <div className="flex items-end gap-1">
                {(['J','A','S','O','N','D','J','F','M','A','M','J'] as const).map((mo, i) => {
                  const now = new Date();
                  const fyYear = parseInt((project.incomeYear ?? 'FY2026').slice(2)) - 1;
                  const fyStart = new Date(`${fyYear}-07-01`);
                  const monthDate = new Date(fyStart.getFullYear(), fyStart.getMonth() + i, 1);
                  const isFuture = monthDate > now;
                  const barColor = isFuture
                    ? 'oklch(0.93 0.006 250)'
                    : (tfa?.locked ? 'oklch(0.55 0.14 155)' : 'oklch(0.72 0.13 75)');
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="w-full rounded-sm" style={{ height: '6px', background: barColor }} />
                      <span className="text-[9px] text-muted-foreground font-mono">{mo}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Panel 3: TFA stage description */}
            <div className="px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground mb-2">
                {tfa ? `TFA Stage · ${formatLabel(tfa.stage)}` : 'Status'}
              </p>
              <p className="text-[12px] text-muted-foreground leading-relaxed">
                {tfa
                  ? tfa.locked
                    ? 'TFA assessment is locked. R&D eligibility determined. Classification is active.'
                    : `Panel progressing through ${formatLabel(tfa.stage)}. Lock the TFA to enable classification.`
                  : 'Create a TFA panel to begin the R&D eligibility assessment for this project.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Classification / backfill status */}
      {(project.lastBackfilledAt || project.lastClassifiedAt || (project.pendingCount ?? 0) > 0) && (
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {project.lastBackfilledAt && (
            <span>Backfilled {relativeTime(project.lastBackfilledAt)}</span>
          )}
          {project.lastClassifiedAt && (
            <span className="ml-2">Classified {relativeTime(project.lastClassifiedAt)}</span>
          )}
          {(project.pendingCount ?? 0) > 0 && project.status !== 'no_rd' && (
            <span className="ml-2 text-amber-600">{project.pendingCount} entries pending classification</span>
          )}
        </div>
      )}

      {/* Product Group */}
      <ProductGroupField project={project} onSaveFlash={onSaveFlash} />

      {/* Aliases / codenames */}
      <AliasesField project={project} onSaveFlash={onSaveFlash} />

      {/* Quick actions */}
      <QuickActions
        projectId={project.id}
        onFlash={onSaveFlash}
        onNavigateTab={onNavigateTab}
      />

      {/* TFA — checklist + full interactive panel (hidden for non-R&D projects) */}
      {project.status !== 'no_rd' && (<>
        {tfaLoading && <div className="h-24 rounded-lg skeleton" />}
        {!tfaLoading && tfa && (<>
          <TfaProgressChecklist tfa={tfa} />
          <TfaPanel tfa={tfa} projectId={project.id} onSaveFlash={onSaveFlash} />
        </>)}
        {!tfaLoading && !tfa && (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <BarChart3 className="h-8 w-8 mx-auto text-muted-foreground/40" aria-hidden="true" />
            <p className="mt-3 text-sm text-muted-foreground">No TFA panel created yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Create one to begin the R&D eligibility assessment.
            </p>
            <Button
              onClick={onCreateTfa}
              disabled={createTfaPending}
              className="mt-4"
            >
              <Plus className="h-4 w-4" />
              {createTfaPending ? 'Creating...' : 'Create TFA Panel'}
            </Button>
          </div>
        )}
      </>)}

      {/* AusIndustry Defensibility */}
      <DefensibilityWidget projectId={project.id} />

      {/* SDLC stage breakdown (per-project, indicative recorded work time) */}
      {project.incomeYear && (
        <SdlcBreakdown incomeYear={project.incomeYear} projectId={project.id} />
      )}

      {/* PRD document + activities preview */}
      <PrdViewerCard projectId={project.id} />
      <ActivitiesPreview projectId={project.id} />

      {/* Grant matches */}
      <div className="rounded-lg border p-4 space-y-3">
        <h3 className="font-medium flex items-center gap-2">
          <Award className="h-4 w-4" aria-hidden="true" />
          Grant Matches
        </h3>
        {grantsLoading ? (
          <div className="h-12 rounded-lg skeleton" />
        ) : grantMatches.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No grant matches yet.{' '}
            {project.status === 'no_rd' ? 'Run a grant match from the Grants page.' : tfaLocked ? 'Navigate to Grants to run a match.' : 'Lock the TFA first to enable matching.'}
          </p>
        ) : (
          <div className="space-y-2">
            {grantMatches.map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  {recIcon(m.recommendation)}
                  <span className="text-sm font-medium truncate">{formatLabel(m.recommendation)}</span>
                  {m.alignment && (
                    <span className="text-xs text-muted-foreground truncate hidden md:inline">{m.alignment}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded">
                    {parseFloat(m.matchScore).toFixed(0)}%
                  </span>
                  <Link
                    href={`/grants/${m.grantId}`}
                    className="text-xs text-primary hover:underline"
                  >
                    Prepare & Apply
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Timewriting tab — project-scoped review UI
// ─────────────────────────────────────────────────────────

type TimewritingEntry = {
  id: string;
  startTime: string;
  endTime: string;
  reviewStatus: string;
  rdPercentage: string | null;
  reviewedAt?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  activityId: string | null;
  source: string | null;
  metadata: { repoOwner?: string; repoName?: string; commitMessages?: string[]; developerEmails?: string[]; developerNames?: string[] } | null;
  classification: ClassificationResult | null;
};

function formatTimewritingDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
}

function formatTimeRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  if (sameDay) {
    const t = (d: Date) => d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    return `${t(start)}–${t(end)}`;
  }
  const d = (d: Date) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  return `${d(start)} – ${d(end)}`;
}

function ProjectTimewritingTab({ projectId, tfaLocked, projectStatus }: { projectId: string; tfaLocked: boolean; projectStatus: string }) {
  const [selectedEntry, setSelectedEntry] = useState<TimewritingEntry | null>(null);
  const [calView, setCalView] = useState<'list' | 'calendar'>('list');
  const [calMonth, setCalMonth] = useState<Date>(() => new Date());
  const { data: classifyingData, refetch: refetchClassifying } = useGetTimeEntries({ projectId, reviewStatus: 'classifying' });
  const { data: pendingData, isLoading: pendingLoading, error: pendingError, refetch: refetchPending } = useGetTimeEntries({ projectId, reviewStatus: 'pending' });
  const { data: approvedData, refetch: refetchApproved } = useGetTimeEntries({ projectId, reviewStatus: 'approved' });
  const { data: rejectedData, refetch: refetchRejected } = useGetTimeEntries({ projectId, reviewStatus: 'rejected' });
  const { data: activitiesData } = useGetProjectActivities(projectId);

  const approveEntry = useApproveTimeEntry();
  const rejectEntry = useRejectTimeEntry();
  const undoRejection = useUndoTimeEntryRejection();

  const [isClassifying, setIsClassifying] = useState(false);
  const [classifyProgress, setClassifyProgress] = useState<{ pending: number; classifying: number; approved: number } | null>(null);
  const [classifyError, setClassifyError] = useState<string | null>(null);
  const classifyPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function stopClassifyPoll() {
    if (classifyPollRef.current) {
      clearInterval(classifyPollRef.current);
      classifyPollRef.current = null;
    }
  }

  useEffect(() => {
    return () => stopClassifyPoll();
  }, []);

  const handleClassify = async () => {
    if (isClassifying) return;
    setIsClassifying(true);
    setClassifyProgress(null);
    setClassifyError(null);
    stopClassifyPoll();

    try {
      const result = await customInstance<{ data: { queued: boolean } }>({
        url: `/api/projects/${projectId}/classify-all`,
        method: 'POST',
      });

      if (!result.data.queued) {
        // Local dev fallback — synchronous result, no polling needed
        setIsClassifying(false);
        void refetchPending();
        void refetchApproved();
        void refetchClassifying();
        return;
      }

      // SQS path — poll until done (10s interval to avoid rate limiting)
      classifyPollRef.current = setInterval(() => {
        void (async () => {
          try {
            const status = await customInstance<{ data: { pending: number; classifying: number; approved: number } }>({
              url: `/api/projects/${projectId}/classify-status`,
              method: 'GET',
            });
            setClassifyProgress(status.data);

            if (status.data.pending === 0 && status.data.classifying === 0) {
              stopClassifyPoll();
              setIsClassifying(false);
              void refetchPending();
              void refetchApproved();
              void refetchClassifying();
            }
          } catch (err: unknown) {
            // 429 rate-limited — poll errors are non-fatal, keep polling
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status !== 429) {
              // Non-rate-limit errors: stop after repeated failures (handled by keeping polling)
            }
          }
        })();
      }, 10000);
    } catch {
      setIsClassifying(false);
      setClassifyError('Classification request failed. Please try again.');
      stopClassifyPoll();
    }
  };

  const classifying = (classifyingData?.data?.items ?? []) as unknown as TimewritingEntry[];
  const pending = (pendingData?.data?.items ?? []) as unknown as TimewritingEntry[];
  const approved = (approvedData?.data?.items ?? []) as unknown as TimewritingEntry[];
  const rejected = (rejectedData?.data?.items ?? []) as unknown as TimewritingEntry[];

  const unclassifiedPending = pending.filter((e) => !e.classification);

  const refetchAll = () => {
    void refetchPending();
    void refetchApproved();
    void refetchRejected();
    void refetchClassifying();
  };

  const activityMap = new Map<string, Activity>(
    (activitiesData?.data?.items ?? []).map((a: Activity) => [a.id, a]),
  );

  // Approved: sort by confidence ascending — shakiest entries first.
  const sortedApproved = [...approved].sort((a, b) => {
    const confA = a.classification ? (parseFloat(String(a.classification.confidence)) || 999) : 999;
    const confB = b.classification ? (parseFloat(String(b.classification.confidence)) || 999) : 999;
    return confA - confB;
  });

  // All entries combined with a _status tag (for calendar view)
  type TaggedEntry = TimewritingEntry & { _status: 'pending' | 'approved' | 'rejected' | 'classifying' };
  const allTaggedEntries = useMemo<TaggedEntry[]>(() => [
    ...pending.map((e) => ({ ...e, _status: 'pending' as const })),
    ...approved.map((e) => ({ ...e, _status: 'approved' as const })),
    ...rejected.map((e) => ({ ...e, _status: 'rejected' as const })),
    ...classifying.map((e) => ({ ...e, _status: 'classifying' as const })),
  ], [pending, approved, rejected, classifying]);

  // Group by calendar date (YYYY-MM-DD of startTime)
  const entriesByDay = useMemo(() => {
    const map = new Map<string, TaggedEntry[]>();
    for (const e of allTaggedEntries) {
      const key = e.startTime.slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }
    // Sort each day's entries by start time
    for (const arr of map.values()) arr.sort((a, b) => a.startTime.localeCompare(b.startTime));
    return map;
  }, [allTaggedEntries]);

  function switchToCalendar() {
    if (allTaggedEntries.length > 0) {
      const mostRecent = allTaggedEntries.reduce((best, e) =>
        e.startTime > best ? e.startTime : best, allTaggedEntries[0]!.startTime,
      );
      setCalMonth(new Date(mostRecent));
    }
    setCalView('calendar');
  }

  function renderCalendar() {
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: Array<{ date: Date; inMonth: boolean }> = [];
    for (let i = firstDow - 1; i >= 0; i--) cells.push({ date: new Date(year, month, -i), inMonth: false });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d), inMonth: true });
    const rem = (7 - (cells.length % 7)) % 7;
    for (let i = 1; i <= rem; i++) cells.push({ date: new Date(year, month + 1, i), inMonth: false });

    const todayStr = new Date().toISOString().slice(0, 10);
    const monthLabel = calMonth.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

    return (
      <div className="space-y-2">
        {/* Month navigation */}
        <div className="flex items-center justify-between pb-1">
          <button
            onClick={() => setCalMonth(new Date(year, month - 1, 1))}
            className="p-1.5 rounded hover:bg-muted transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <span className="text-sm font-semibold">{monthLabel}</span>
          <button
            onClick={() => setCalMonth(new Date(year, month + 1, 1))}
            className="p-1.5 rounded hover:bg-muted transition-colors"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 text-center">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
            <div key={d} className="text-[10px] font-medium text-muted-foreground py-1">{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden border">
          {cells.map(({ date, inMonth }) => {
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            const dayEntries = entriesByDay.get(key) ?? [];
            const isToday = key === todayStr;
            return (
              <div
                key={key}
                className={`bg-background p-1 min-h-[60px] ${!inMonth ? 'bg-muted/20' : ''}`}
              >
                <span className={`text-[10px] leading-none font-medium ${
                  isToday ? 'text-primary font-bold' : !inMonth ? 'text-muted-foreground/30' : 'text-muted-foreground'
                }`}>
                  {date.getDate()}
                </span>
                <div className="mt-0.5 space-y-px">
                  {dayEntries.slice(0, 4).map((e) => {
                    const dh = (new Date(e.endTime).getTime() - new Date(e.startTime).getTime()) / 3_600_000;
                    const name = formatDeveloper(
                      e.userName ?? (e.metadata as { developerNames?: string[] } | null)?.developerNames?.[0],
                      e.userEmail ?? (e.metadata as { developerEmails?: string[] } | null)?.developerEmails?.[0],
                    );
                    const initials = name.split(' ').filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase() || '??';
                    const chip =
                      e._status === 'approved' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' :
                      e._status === 'pending' ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' :
                      e._status === 'classifying' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' :
                      'bg-slate-100 text-slate-500 hover:bg-slate-200';
                    return (
                      <button
                        key={e.id}
                        onClick={() => setSelectedEntry(e)}
                        className={`w-full text-left rounded px-1 py-px text-[9px] font-medium leading-tight truncate transition-colors ${chip}`}
                        title={`${name} · ${dh.toFixed(1)}h · ${e._status}`}
                      >
                        {initials} {dh >= 1 ? `${dh.toFixed(1)}h` : `${Math.round(dh * 60)}m`}
                      </button>
                    );
                  })}
                  {dayEntries.length > 4 && (
                    <div className="text-[9px] text-muted-foreground pl-1">+{dayEntries.length - 4}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 pt-0.5 flex-wrap">
          {[
            { label: 'Approved', cls: 'bg-emerald-200' },
            { label: 'Pending', cls: 'bg-amber-200' },
            { label: 'Classifying', cls: 'bg-blue-200' },
            { label: 'Rejected', cls: 'bg-slate-200' },
          ].map(({ label, cls }) => (
            <div key={label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={`inline-block w-2.5 h-2.5 rounded-sm ${cls}`} />
              {label}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // R&D hours from approved entries
  const totalRdHours = approved.reduce((acc, e) => {
    if (e.rdPercentage == null) return acc;
    const durationMs = new Date(e.endTime).getTime() - new Date(e.startTime).getTime();
    return acc + (durationMs / 3_600_000) * (parseFloat(e.rdPercentage) / 100);
  }, 0);

  function sourceBadge(source: string | null): ReactNode {
    if (source === 'claude_code') {
      return <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-violet-100 text-violet-700 border border-violet-200">CC</span>;
    }
    if (source === 'github') {
      return <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200">GitHub</span>;
    }
    return null;
  }

  // Renders a single time entry row shared across all sections.
  // showActions: what action buttons to render on the right.
  function renderEntryRow(
    entry: TimewritingEntry,
    showActions: 'approve-reject' | 'reject-only' | 'undo' | 'none',
    opts: { dimmed?: boolean } = {},
  ): ReactNode {
    const cls = entry.classification;
    const rdPct = cls ? parseFloat(String(cls.rdPercentage)) : null;
    const confidence = cls ? (parseFloat(String(cls.confidence)) || null) : null;
    const actName = entry.activityId ? (activityMap.get(entry.activityId)?.name ?? null) : null;
    // rationaleSlim is injected by the API — first 140 chars of the AI rationale
    const rationaleSlim = (cls as (ClassificationResult & { rationaleSlim?: string | null }) | null)?.rationaleSlim ?? null;
    const description = rationaleSlim ?? actName;
    const durationMs = new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime();
    const durationHrs = durationMs / 3_600_000;
    const durationLabel = durationHrs >= 1 ? `${durationHrs.toFixed(1)}h` : `${Math.round(durationMs / 60_000)}m`;

    // Color bar: green for R&D portion, muted for remainder
    const barColor = rdPct === null ? '' : rdPct >= 80 ? 'bg-emerald-500' : rdPct >= 40 ? 'bg-amber-400' : 'bg-slate-400';

    return (
      <div
        key={entry.id}
        className={`rounded-lg border bg-card overflow-hidden transition-colors ${opts.dimmed ? 'opacity-60' : ''} ${showActions === 'approve-reject' || showActions === 'reject-only' ? 'cursor-pointer hover:border-primary/30' : ''}`}
        onClick={showActions !== 'none' ? () => setSelectedEntry(entry) : undefined}
      >
        {/* Row 1: developer · date · duration | source badge */}
        <div className="flex items-start justify-between px-3 pt-3 pb-1 gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold">
                {formatDeveloper(entry.userName ?? entry.metadata?.developerNames?.[0], entry.userEmail ?? entry.metadata?.developerEmails?.[0])}
              </span>
              {sourceBadge(entry.source)}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatTimewritingDate(entry.startTime)} · {formatTimeRange(entry.startTime, entry.endTime)}
            </p>
          </div>
          <span className="text-base font-semibold tabular-nums shrink-0">{durationLabel}</span>
        </div>

        {/* Row 2: 1-line description */}
        {description && (
          <p className="text-xs text-muted-foreground px-3 pb-1 leading-snug line-clamp-1" title={description}>
            {description}
          </p>
        )}

        {/* Row 3: R&D bar + stats + action buttons */}
        <div className="px-3 pb-3 mt-1.5">
          {/* Color bar */}
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            {cls ? (
              <div
                className={`h-full rounded-full transition-all duration-300 ${barColor}`}
                style={{ width: `${Math.min(100, Math.max(0, rdPct ?? 0))}%` }}
                aria-label={`${rdPct?.toFixed(0) ?? 0}% R&D`}
              />
            ) : (
              <div className="h-full w-full rounded-full bg-amber-200" />
            )}
          </div>
          {/* Stats row + actions */}
          <div className="flex items-center justify-between mt-1.5 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {cls ? (
                <>
                  <span className={`text-xs font-medium tabular-nums shrink-0 ${
                    rdPct !== null && rdPct >= 80 ? 'text-emerald-700' :
                    rdPct !== null && rdPct >= 40 ? 'text-amber-600' : 'text-muted-foreground'
                  }`}>
                    {rdPct !== null ? `${rdPct.toFixed(0)}% R&D` : '—'}
                  </span>
                  {confidence !== null && (
                    <span className={`text-xs tabular-nums shrink-0 ${
                      confidence >= 85 ? 'text-emerald-600' : confidence >= 70 ? 'text-amber-600' : 'text-red-500'
                    }`}>
                      {confidence.toFixed(0)}% conf
                    </span>
                  )}
                  {actName && !rationaleSlim && (
                    <span className="text-xs text-muted-foreground truncate">{actName}</span>
                  )}
                </>
              ) : (
                <span className="text-xs text-amber-600 shrink-0">Awaiting classification</span>
              )}
            </div>
            {/* Actions */}
            <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
              {showActions === 'approve-reject' && (
                <>
                  <button
                    className="text-xs text-muted-foreground hover:text-destructive px-2 py-0.5 rounded hover:bg-destructive/10 transition-colors disabled:opacity-40"
                    disabled={rejectEntry.isPending}
                    onClick={() => rejectEntry.mutate(entry.id, { onSuccess: refetchAll })}
                  >
                    Reject
                  </button>
                  <button
                    className="text-xs font-medium text-primary-foreground bg-primary hover:bg-primary/90 px-2.5 py-0.5 rounded transition-colors disabled:opacity-40"
                    disabled={approveEntry.isPending}
                    onClick={() => approveEntry.mutate({ id: entry.id }, { onSuccess: refetchAll })}
                  >
                    Approve
                  </button>
                </>
              )}
              {showActions === 'reject-only' && (
                <button
                  className="text-xs text-muted-foreground hover:text-destructive transition-colors disabled:opacity-40"
                  disabled={rejectEntry.isPending}
                  onClick={() => rejectEntry.mutate(entry.id, { onSuccess: refetchAll })}
                >
                  Reject
                </button>
              )}
              {showActions === 'undo' && (
                <button
                  className="text-xs text-muted-foreground underline hover:text-foreground transition-colors disabled:opacity-40"
                  disabled={undoRejection.isPending}
                  onClick={() => undoRejection.mutate({ id: entry.id }, { onSuccess: refetchAll })}
                >
                  Undo
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalPendingCount = pending.length + classifying.length;

  return (
    <div className="space-y-6">

      {/* ── Stats + view toggle ── */}
      <div className="flex items-start gap-3">
        <div className="grid grid-cols-3 gap-3 flex-1">
          <div className="rounded-lg border bg-card px-3 py-2.5">
            <p className="text-xs text-muted-foreground">Pending review</p>
            <p className="text-xl font-semibold tabular-nums mt-0.5">{totalPendingCount > 0 ? totalPendingCount : '—'}</p>
          </div>
          <div className="rounded-lg border bg-card px-3 py-2.5">
            <p className="text-xs text-muted-foreground">Approved</p>
            <p className="text-xl font-semibold tabular-nums mt-0.5">{approved.length > 0 ? approved.length : '—'}</p>
          </div>
          <div className="rounded-lg border bg-card px-3 py-2.5">
            <p className="text-xs text-muted-foreground">R&amp;D hours</p>
            <p className="text-xl font-semibold tabular-nums mt-0.5">
              {totalRdHours > 0 ? `${totalRdHours.toFixed(1)}h` : '—'}
            </p>
          </div>
        </div>
        {/* View toggle */}
        <div className="flex rounded-lg border overflow-hidden shrink-0">
          <button
            onClick={() => setCalView('list')}
            className={`px-2.5 py-2 flex items-center gap-1.5 text-xs font-medium transition-colors ${calView === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
            aria-label="List view"
          >
            <List className="h-3.5 w-3.5" aria-hidden="true" />
            List
          </button>
          <button
            onClick={switchToCalendar}
            className={`px-2.5 py-2 flex items-center gap-1.5 text-xs font-medium transition-colors border-l ${calView === 'calendar' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
            aria-label="Calendar view"
          >
            <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
            Calendar
          </button>
        </div>
      </div>

      {/* ── Calendar view ── */}
      {calView === 'calendar' && renderCalendar()}

      {calView === 'list' && (<>
      {/* ── Classify action bar ── */}
      {(tfaLocked && totalPendingCount > 0 && projectStatus !== 'no_rd') ? (
        <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3">
          <div className="flex-1 min-w-0">
            {isClassifying ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 text-primary" aria-hidden="true" />
                  <span className="font-medium">Classifying with AI…</span>
                </div>
                {classifyProgress && (
                  <p className="text-xs text-muted-foreground">
                    {classifyProgress.classifying > 0
                      ? `Classifying… ${classifyProgress.classifying} in progress, ${classifyProgress.pending} pending`
                      : classifyProgress.pending > 0
                      ? `${classifyProgress.pending} pending in queue…`
                      : 'Finalising…'}
                  </p>
                )}
              </div>
            ) : classifyError ? (
              <p className="text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {classifyError}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {unclassifiedPending.length > 0
                  ? `${unclassifiedPending.length} ${unclassifiedPending.length === 1 ? 'entry needs' : 'entries need'} AI classification`
                  : classifying.length > 0
                  ? `${classifying.length} entries stuck classifying — click to reset and retry`
                  : 'All entries classified'}
              </p>
            )}
          </div>
          {(!isClassifying && (unclassifiedPending.length > 0 || classifying.length > 0)) ? (
            <button
              type="button"
              onClick={() => void handleClassify()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 shrink-0"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              {(classifying.length > 0 && unclassifiedPending.length === 0)
                ? `Reset ${classifying.length} stuck + retry`
                : `AI-classify ${unclassifiedPending.length}`}
            </button>
          ) : null}
        </div>
      ) : null}

      {(tfaLocked === false && pending.length > 0 && projectStatus !== 'no_rd')
        ? <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">Lock the TFA to enable AI classification.</div>
        : null}

      {/* ── Loading / error ── */}
      {pendingLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-[76px] w-full rounded-lg skeleton" />)}
        </div>
      ) : null}
      {pendingError ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load time entries.</span>
          <Button variant="ghost" size="sm" onClick={() => void refetchPending()} className="ml-auto">
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Retry
          </Button>
        </div>
      ) : null}

      {/* ── Stuck classifying ── */}
      {classifying.length > 0 && !isClassifying && (
        <section aria-labelledby="proj-classifying-heading">
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-center justify-between gap-3 text-sm text-amber-800 mb-2">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                <strong>{classifying.length}</strong> {classifying.length === 1 ? 'entry is' : 'entries are'} stuck from an interrupted run.
              </span>
            </div>
            <Button variant="secondary" size="sm" onClick={() => void handleClassify()}>
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Reset &amp; retry
            </Button>
          </div>
          <div className="space-y-2">
            {classifying.map((e) => renderEntryRow(e, 'none', { dimmed: true }))}
          </div>
        </section>
      )}

      {/* ── Awaiting review ── */}
      {!pendingLoading && !pendingError && pending.length > 0 && (
        <section aria-labelledby="proj-pending-heading">
          <div className="flex items-center gap-2 mb-2">
            <h2 id="proj-pending-heading" className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Awaiting review
            </h2>
            <Badge variant="warning">{pending.length}</Badge>
          </div>
          <div className="space-y-2">
            {pending.map((e) => renderEntryRow(e, 'approve-reject'))}
          </div>
        </section>
      )}

      {!pendingLoading && !pendingError && pending.length === 0 && classifying.length === 0 && approved.length === 0 && rejected.length === 0 && (
        <div className="text-center py-10 rounded-lg border border-dashed">
          <Clock className="h-8 w-8 mx-auto text-muted-foreground mb-2" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">No time entries yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Sync historical activity from the Signals tab.</p>
        </div>
      )}

      {/* ── Approved ── */}
      {sortedApproved.length > 0 && (
        <section aria-labelledby="proj-approved-heading">
          <div className="flex items-center gap-2 mb-2">
            <h2 id="proj-approved-heading" className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Approved
            </h2>
            <Badge variant="positive">{sortedApproved.length}</Badge>
            <span className="text-xs text-muted-foreground ml-auto">low-confidence first · click for breakdown</span>
          </div>
          <div className="space-y-2">
            {sortedApproved.map((e) => renderEntryRow(e, 'reject-only'))}
          </div>
        </section>
      )}

      {/* ── Rejected ── */}
      {rejected.length > 0 && (
        <section aria-labelledby="proj-rejected-heading">
          <h2 id="proj-rejected-heading" className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Rejected
          </h2>
          <div className="space-y-2">
            {rejected.map((e) => renderEntryRow(e, 'undo', { dimmed: true }))}
          </div>
        </section>
      )}

      </>)}

      {selectedEntry && (
        <TimeEntryDetailPanel
          entry={{
            ...selectedEntry,
            source: (selectedEntry as TimewritingEntry & { source?: string }).source ?? 'github',
            classification: selectedEntry.classification
              ? {
                  label: selectedEntry.classification.label,
                  confidence: String(selectedEntry.classification.confidence),
                  rdPercentage: String(selectedEntry.classification.rdPercentage),
                  rationale: selectedEntry.classification.rationale ?? null,
                }
              : null,
          }}
          open={!!selectedEntry}
          onClose={() => setSelectedEntry(null)}
        />
      )}
    </div>
  );
}

type ProjectTab = 'overview' | 'timeline' | 'timewriting' | 'activities' | 'signals' | 'evidence' | 'documents' | 'eligibility';

const TABS: { key: ProjectTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'activities', label: 'Activities' },
  { key: 'timewriting', label: 'Time' },
  { key: 'signals', label: 'Signals' },
  { key: 'eligibility', label: 'Eligibility' },
  { key: 'evidence', label: 'Evidence' },
  { key: 'documents', label: 'Documents' },
];

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const search = useSearch();
  const queryClient = useQueryClient();
  const { data: projectData, isLoading: projectLoading } = useGetProject(id ?? '');
  const [tfaId, setTfaId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ProjectTab>(() => {
    const params = new URLSearchParams(search);
    const tab = params.get('tab');
    if (tab && TABS.some((t) => t.key === tab)) {
      return tab as ProjectTab;
    }
    return 'overview';
  });

  useEffect(() => {
    const params = new URLSearchParams(search);
    const tab = params.get('tab');
    if (tab && TABS.some((t) => t.key === tab)) {
      setActiveTab(tab as ProjectTab);
    }
  }, [search]);
  const [saveFlash, setSaveFlash] = useState<string | null>(null);

  // Rename state
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  const updateProject = useUpdateProject();

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const deleteProject = useDeleteProject();

  // Close project state
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeStatus, setCloseStatus] = useState<'completed' | 'cancelled'>('completed');
  const [closeReason, setCloseReason] = useState('');
  const closeProject = useCloseProject();

  const createTfa = useCreateProjectTfa();

  const project = projectData?.data as (Project & { tfaPanelId?: string | null }) | undefined;
  const resolvedTfaId = tfaId ?? project?.tfaPanelId ?? null;
  const tfaQueryKey = getGetTfaQueryKey(resolvedTfaId ?? '');
  const { data: tfaData, isLoading: tfaLoading } = useGetTfa(resolvedTfaId ?? '', {
    query: {
      queryKey: tfaQueryKey,
      refetchInterval: (q) => q.state.data?.data?.aiDraftStatus === 'pending' ? 3000 : false,
    },
  });

  const tfa = tfaData?.data;
  const tfaLocked = tfa?.locked ?? false;

  const showSaveFlash = useCallback((message: string) => {
    setSaveFlash(message);
    setTimeout(() => setSaveFlash(null), 3000);
  }, []);

  const startRename = useCallback(() => {
    if (!project) return;
    setRenameValue(project.name);
    setRenaming(true);
    setTimeout(() => renameInputRef.current?.select(), 30);
  }, [project]);

  const commitRename = useCallback(() => {
    if (!project || !renameValue.trim() || renameValue.trim() === project.name) {
      setRenaming(false);
      return;
    }
    updateProject.mutate(
      { id: project.id, data: { name: renameValue.trim() } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey(project.id) });
          showSaveFlash('Renamed');
          setRenaming(false);
        },
        onError: () => setRenaming(false),
      }
    );
  }, [project, renameValue, updateProject, queryClient, showSaveFlash]);

  const handleDeleteProject = useCallback(() => {
    if (!project) return;
    deleteProject.mutate(
      { id: project.id },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetProjectsQueryKey() });
          navigate('/projects');
        },
        onError: (err) => {
          const msg = (err as unknown as { response?: { data?: { error?: { message?: string } } } })
            ?.response?.data?.error?.message ?? 'Failed to delete project';
          showSaveFlash(msg);
          setDeleteConfirm(false);
        },
      }
    );
  }, [project, deleteProject, navigate, showSaveFlash]);

  const handleCloseProject = useCallback(() => {
    if (!project || !closeReason.trim()) return;
    closeProject.mutate(
      { id: project.id, data: { status: closeStatus, closureReason: closeReason.trim() } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey(project.id) });
          void queryClient.invalidateQueries({ queryKey: getGetProjectsQueryKey() });
          setCloseDialogOpen(false);
          setCloseReason('');
          showSaveFlash(closeStatus === 'completed' ? 'Project marked as completed' : 'Project cancelled');
        },
        onError: (err) => {
          const msg = (err as unknown as { response?: { data?: { error?: { message?: string } } } })
            ?.response?.data?.error?.message ?? 'Failed to close project';
          showSaveFlash(msg);
        },
      }
    );
  }, [project, closeProject, closeStatus, closeReason, queryClient, showSaveFlash]);

  if (projectLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 rounded-lg skeleton" />
        <div className="h-40 w-full rounded-lg skeleton" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <AlertCircle className="h-4 w-4" />
        Project not found.
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* ── Sticky header ─────────────────────────────────────── */}
      <div className="sticky top-0 z-10 bg-background border-b pb-0">
        {/* Breadcrumb back button */}
        <div className="px-1 pt-2 pb-1">
          <Link
            href="/projects"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Projects
          </Link>
        </div>
        <div className="flex items-center justify-between px-1 pt-1 pb-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {renaming ? (
              <div className="flex items-center gap-1.5 min-w-0 flex-1 max-w-sm">
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename();
                    if (e.key === 'Escape') setRenaming(false);
                  }}
                  onBlur={commitRename}
                  className="text-2xl font-semibold tracking-tight bg-transparent border-b-2 border-primary outline-none w-full min-w-0"
                  autoFocus
                />
                <button
                  onClick={commitRename}
                  disabled={updateProject.isPending}
                  className="h-7 w-7 shrink-0 rounded-md border border-green-300 bg-green-50 text-green-700 flex items-center justify-center hover:bg-green-100"
                  aria-label="Save name"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setRenaming(false)}
                  className="h-7 w-7 shrink-0 rounded-md border border-border text-muted-foreground flex items-center justify-center hover:bg-accent"
                  aria-label="Cancel rename"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-semibold tracking-tight truncate">{project.name}</h1>
                <button
                  onClick={startRename}
                  className="h-6 w-6 shrink-0 rounded text-muted-foreground hover:text-foreground hover:bg-accent flex items-center justify-center"
                  aria-label="Rename project"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </>
            )}
            {project.incomeYear && (
              <span className="text-xs font-mono text-muted-foreground shrink-0">{formatFy(project.incomeYear)}</span>
            )}
            {/* TFA status pill — always visible */}
            {!tfaLoading && (
              tfa?.locked ? (
                <Badge variant="positive" className="shrink-0">
                  <Lock className="h-3 w-3" />
                  TFA locked
                </Badge>
              ) : tfa ? (
                <Badge variant="warning" className="shrink-0">
                  <AlertTriangle className="h-3 w-3" />
                  TFA in progress
                </Badge>
              ) : (
                <Badge variant="default" className="shrink-0">
                  No TFA
                </Badge>
              )
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            {project.description && (
              <p className="hidden lg:block text-xs text-muted-foreground truncate max-w-xs">{project.description}</p>
            )}
            {/* Close project — only for active/no_rd projects */}
            {(project.status === 'active' || project.status === 'no_rd') && !deleteConfirm && (
              <button
                onClick={() => { setCloseStatus('completed'); setCloseReason(''); setCloseDialogOpen(true); }}
                className="h-7 px-2 rounded-md border border-border text-xs text-muted-foreground hover:border-amber-400 hover:text-amber-700 flex items-center gap-1.5 transition-colors"
                title="Close project"
              >
                <X className="h-3 w-3" />
                Close
              </button>
            )}
            {/* Delete project */}
            {!deleteConfirm ? (
              <button
                onClick={() => setDeleteConfirm(true)}
                className="h-7 w-7 rounded-md border border-border text-muted-foreground hover:border-destructive hover:text-destructive flex items-center justify-center transition-colors"
                aria-label="Delete project"
                title="Delete project"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            ) : (
              <div className="flex items-center gap-1.5 rounded-md border border-destructive/50 bg-destructive/5 px-2 py-1">
                <span className="text-xs text-destructive">Delete project?</span>
                <button
                  onClick={handleDeleteProject}
                  disabled={deleteProject.isPending}
                  className="h-5 px-2 rounded bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors"
                >
                  {deleteProject.isPending ? 'Deleting…' : 'Yes, delete'}
                </button>
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="h-5 w-5 rounded text-muted-foreground hover:bg-accent flex items-center justify-center"
                  aria-label="Cancel delete"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
              }`}
              {...(tab.key === 'activities' && { 'data-tour': 'activities-tab' })}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ───────────────────────────────────────── */}
      <div key={activeTab} className="pt-5 animate-tab">
        {activeTab === 'overview' && (
          <OverviewTab
            project={project}
            tfa={tfa ?? null}
            tfaLoading={tfaLoading}
            tfaLocked={tfaLocked}
            onCreateTfa={() =>
              createTfa.mutate(
                { projectId: project.id },
                {
                  onSuccess: (res) => {
                    setTfaId(res?.data?.id ?? null);
                    showSaveFlash('TFA panel created');
                  },
                  onError: () => {
                    // 409 = TFA already exists (e.g. created before this session loaded).
                    // Invalidate project so tfaPanelId is re-fetched, then the TFA will load.
                    queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey(project.id) });
                  },
                }
              )
            }
            createTfaPending={createTfa.isPending}
            onSaveFlash={showSaveFlash}
            onNavigateTab={(tab) => setActiveTab(tab as ProjectTab)}
          />
        )}

        {saveFlash && (
          <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
            {saveFlash}
          </div>
        )}

        {activeTab === 'timeline' && (
          <TimelineTab projectId={project.id} />
        )}

        {activeTab === 'timewriting' && (
          <ProjectTimewritingTab projectId={project.id} tfaLocked={tfaLocked} projectStatus={project.status ?? 'active'} />
        )}

        {activeTab === 'activities' && (
          <ActivitiesSection projectId={project.id} />
        )}

        {activeTab === 'signals' && (
          <SignalsTab projectId={project.id} />
        )}

        {activeTab === 'evidence' && (
          <ProjectEvidenceSection projectId={project.id} />
        )}

        {activeTab === 'documents' && (
          <DocumentTimeline projectId={project.id} />
        )}

        {activeTab === 'eligibility' && (
          <EligibilityTab projectId={project.id} />
        )}
      </div>

      {/* Close project modal */}
      {closeDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setCloseDialogOpen(false)}
        >
          <div
            className="bg-background border border-border rounded-xl shadow-xl w-full max-w-md mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-base font-semibold mb-1">Close project</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Closed projects are archived and excluded from active R&D tracking. This action can be reversed by editing the project status.
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-foreground block mb-1.5">Closure type</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCloseStatus('completed')}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${
                      closeStatus === 'completed'
                        ? 'border-primary bg-primary/5 text-foreground'
                        : 'border-border text-muted-foreground hover:border-muted-foreground/50'
                    }`}
                  >
                    <div className="font-medium">Completed</div>
                    <div className="text-xs opacity-70 mt-0.5">Work finished successfully</div>
                  </button>
                  <button
                    onClick={() => setCloseStatus('cancelled')}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${
                      closeStatus === 'cancelled'
                        ? 'border-primary bg-primary/5 text-foreground'
                        : 'border-border text-muted-foreground hover:border-muted-foreground/50'
                    }`}
                  >
                    <div className="font-medium">Cancelled</div>
                    <div className="text-xs opacity-70 mt-0.5">Work stopped or abandoned</div>
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="close-reason" className="text-xs font-medium text-foreground block mb-1.5">
                  Closure reason <span className="text-destructive">*</span>
                </label>
                <textarea
                  id="close-reason"
                  value={closeReason}
                  onChange={(e) => setCloseReason(e.target.value)}
                  rows={3}
                  placeholder="Briefly describe why this project is being closed…"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setCloseDialogOpen(false)}
                className="h-8 px-3 rounded-md border border-border text-sm text-muted-foreground hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseProject}
                disabled={!closeReason.trim() || closeProject.isPending}
                className="h-8 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {closeProject.isPending ? 'Closing…' : `Close as ${closeStatus}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
